import React, { useState, useEffect, useMemo, ReactNode } from 'react';
import { getTodayDate } from '../../utils/dateUtils';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';
import { useTicketWorkflow, TicketWorkflowOverrides } from '../../hooks/useTicketWorkflow';
import { useTenantConfig } from '../../contexts/TenantConfigContext';
import AuditHistoryDrawer from '../shared/AuditHistoryDrawer';
import LoadingOverlay from '../shared/LoadingOverlay';
import {
  Container,
  ContentWrapper,
  MainContent,
  PageTitle,
  TicketHeader,
  TicketInfo,
  TicketLabel,
  TicketNumber,
  TicketStatusWrapper,
  StatusValue,
  FormContainer,
  ActionButtonsWrapper,
  ActionButtons,
  ActionButton,
  SecondaryButton,
  SuccessMessage,
  LoadingSpinner,
  ErrorSummary,
  ErrorSummaryTitle,
  ErrorList,
  FullWidthSection,
  ExpanderHeader,
  ExpanderContent,
  AllocationPanel,
  AllocationHeader,
  AllocationTitle,
  ToggleButton,
  AllocationGrid,
  AllocationGridHeader,
  AllocationRow,
  AllocationInput,
  AllocationSelect,
  AllocationRemoveButton,
  AddAllocationButton,
  AllocationSummary,
  AllocationSummaryLabel,
  AllocationSummaryValue,
  FileUploadArea,
  FileList,
  FileItem,
  FileInfo,
  FileIcon,
  FileDetails,
  FileName,
  FileSize,
  FileRemoveButton,
  FileUploadText,
  TextArea,
  FormGroup,
  FormRow,
  Label,
  Select,
  FieldError
} from './shared/TicketStyledComponents';
// TicketTypeNavigation removed - navigation now handled by InstitutionalTradeEntry/RetailTradeEntry
import { formatAmountWithCommas, parseAmountShorthand } from '../../utils/amountUtils';
import { useRole } from '../../hooks/useRole';
import { canSubmitForPreClear, canApproveRejectTrades } from '../../utils/roleUtils';
import {
  Activity,
  ArrowRightLeft,
  BarChart3,
  CalendarDays,
  ChevronDown,
  ChevronRight,
  CircleDollarSign,
  Gauge,
  ShieldCheck,
  Sigma,
  TrendingUp
} from 'lucide-react';
import ExecutionClearingSection, {
  EC_FIELD_KEYS,
  buildExecutionClearingPayload,
  executionClearingInitialFields,
  getTicketExecutionClearingConfig,
  loadExecutionClearingFields,
  validateExecutionClearingFields
} from './shared/ExecutionClearingSection';
import {
  DerivativeHeroPanel,
  DerivativeSignal,
  DerivativeTone,
  formatDerivativeCurrency,
  formatDerivativeNumber
} from './shared/DerivativeTicketLayout';

// ============================================================================
// TENANT CONTEXT STYLED COMPONENTS (Phase 2 - Institutional vs Retail)
// ============================================================================

const TaxWarningBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  margin-bottom: 16px;
  border-radius: 8px;
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.3);
  color: #f59e0b;
  font-size: 13px;
`;

const ProspectiveImpactPanel = styled.div`
  background: rgba(30, 41, 59, 0.6);
  border: 1px solid rgba(245, 158, 11, 0.2);
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 1.5rem;
`;

const ImpactRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  font-size: 13px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  &:last-child { border-bottom: none; }
`;

const ShortcutFeedback = styled.div<{ $tone: 'success' | 'warning' }>`
  border: 1px solid ${({ $tone }) => $tone === 'success' ? 'rgba(34, 197, 94, 0.35)' : 'rgba(245, 158, 11, 0.45)'};
  background: ${({ $tone }) => $tone === 'success' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(245, 158, 11, 0.1)'};
  color: ${({ $tone }) => $tone === 'success' ? '#86efac' : '#fbbf24'};
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 16px;
  font-size: 13px;
  font-weight: 600;
`;

const OverrideBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(2, 6, 23, 0.82);
  backdrop-filter: blur(8px);
`;

const OverrideDialog = styled.div`
  width: min(760px, 100%);
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  border-radius: 18px;
  border: 1px solid rgba(245, 158, 11, 0.35);
  background: linear-gradient(145deg, #111c2e 0%, #081221 100%);
  box-shadow: 0 30px 90px rgba(0, 0, 0, 0.55);
`;

const OverrideDialogHeader = styled.div`
  display: flex;
  gap: 14px;
  padding: 24px 26px 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const OverrideIcon = styled.div`
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  display: grid;
  place-items: center;
  border-radius: 12px;
  color: #fbbf24;
  background: rgba(245, 158, 11, 0.14);
  border: 1px solid rgba(245, 158, 11, 0.28);
`;

const OverrideBody = styled.div`
  padding: 22px 26px 26px;
`;

const OverrideMetrics = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 18px;
  @media (max-width: 650px) { grid-template-columns: 1fr; }
`;

const OverrideMetric = styled.div`
  padding: 12px 14px;
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.72);
  border: 1px solid rgba(148, 163, 184, 0.16);
  small { display: block; color: #8291a8; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; }
  strong { display: block; margin-top: 5px; color: #f8fafc; font-size: 17px; }
`;

const BreachCard = styled.div`
  padding: 13px 15px;
  margin-top: 9px;
  border-radius: 10px;
  background: rgba(127, 29, 29, 0.16);
  border: 1px solid rgba(248, 113, 113, 0.24);
  color: #fecaca;
  font-size: 13px;
  line-height: 1.45;
`;

const OverrideActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
`;

interface ProspectiveState {
  portfolioId: number;
  currentTotalMv: number;
  prospectiveTotalMv: number;
  tradeIssuerDelta: { key: string; beforePct: number; afterPct: number; deltaPct: number } | null;
  tradeSectorDelta: { key: string; beforePct: number; afterPct: number; deltaPct: number } | null;
  ratingDistribution: {
    igPctBefore: number; hyPctBefore: number; nrPctBefore: number;
    igPctAfter: number; hyPctAfter: number; nrPctAfter: number;
  } | null;
  durationBefore: number;
  durationAfter: number;
  dv01Before: number;
  dv01After: number;
  cashImpact: number;
}

const hasValue = (value: any) => value !== undefined && value !== null && String(value).trim() !== '';

const firstValue = (...values: any[]) => values.find(hasValue);

const formatPercentSignal = (value: any, suffix = '%') => {
  if (!hasValue(value)) return '-';
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return String(value);
  return `${parsed.toLocaleString('en-US', { maximumFractionDigits: 4 })}${suffix}`;
};

const directionLabel = (value: any) => {
  if (!hasValue(value)) return '-';
  return String(value)
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const buildDerivativeHeroConfig = (assetSubClass: string | undefined, formData: any) => {
  const subClass = assetSubClass || '';
  const normalizedSubClass = subClass.toLowerCase();
  const notionalRaw = firstValue(
    formData.notionalAmount,
    formData.fixedNotional,
    formData.trNotional,
    formData.payNotional
  );
  const notional = hasValue(notionalRaw) ? parseAmountShorthand(String(notionalRaw)) : 0;
  const currency = firstValue(
    formData.currency,
    formData.fixedCurrency,
    formData.trCurrency,
    formData.payCurrency,
    formData.premiumCurrency
  ) || 'USD';

  const baseSignals: DerivativeSignal[] = [
    { label: 'Direction', value: directionLabel(formData.direction), accent: true },
  ];

  if (normalizedSubClass.includes('interest rate')) {
    return {
      toneName: 'rates' as DerivativeTone,
      icon: Activity,
      eyebrow: 'Rates Derivatives',
      title: 'Interest Rate Swap',
      description: 'Capture fixed and floating legs, tenor, reset cadence, and execution context in one rates ticket.',
      badges: ['Fixed/float legs', 'Rates exposure', 'Lifecycle ticket'],
      signals: [
        ...baseSignals,
        { label: 'Notional', value: formatDerivativeCurrency(notional, currency), accent: true },
        { label: 'Fixed Rate', value: formatPercentSignal(formData.fixedRate) },
        { label: 'Float Index', value: firstValue(formData.floatIndex, '-') },
      ],
    };
  }

  if (normalizedSubClass.includes('total return')) {
    return {
      toneName: 'trs' as DerivativeTone,
      icon: TrendingUp,
      eyebrow: 'Equity / Credit Derivatives',
      title: 'Total Return Swap',
      description: 'Define reference asset return transfer, financing leg economics, tenor, and execution ownership.',
      badges: ['Reference asset', 'Financing spread', 'Lifecycle ticket'],
      signals: [
        ...baseSignals,
        { label: 'TR Notional', value: formatDerivativeCurrency(notional, currency), accent: true },
        { label: 'Reference', value: firstValue(formData.trReferenceAsset, formData.identifier, '-') },
        { label: 'Financing', value: firstValue(formData.finIndex, '-') },
      ],
    };
  }

  if (normalizedSubClass.includes('currency')) {
    return {
      toneName: 'currency' as DerivativeTone,
      icon: ArrowRightLeft,
      eyebrow: 'FX Derivatives',
      title: 'Cross-Currency Swap',
      description: 'Book pay/receive currencies, initial FX rate, notional exchange, and rate type per leg.',
      badges: ['Pay/receive legs', 'FX reset terms', 'Lifecycle ticket'],
      signals: [
        { label: 'Pay Currency', value: firstValue(formData.payCurrency, '-'), accent: true },
        { label: 'Receive Currency', value: firstValue(formData.recvCurrency, '-'), accent: true },
        { label: 'Pay Notional', value: formatDerivativeCurrency(notional, formData.payCurrency || currency) },
        { label: 'Initial FX', value: firstValue(formData.initialExchangeRate, '-') },
      ],
    };
  }

  if (normalizedSubClass.includes('credit default')) {
    return {
      toneName: 'credit' as DerivativeTone,
      icon: ShieldCheck,
      eyebrow: 'Credit Derivatives',
      title: 'Credit Default Swap',
      description: 'Capture protection direction, reference entity or index, standard coupon, credit events, and clearing terms.',
      badges: ['Protection leg', 'Contingent leg', 'ISDA terms'],
      signals: [
        ...baseSignals,
        { label: 'Notional', value: formatDerivativeCurrency(notional, currency), accent: true },
        { label: 'Reference', value: firstValue(formData.referenceEntity, formData.indexName, '-') },
        { label: 'Coupon', value: hasValue(formData.fixedRateBps) ? `${formData.fixedRateBps} bps` : '-' },
      ],
    };
  }

  if (normalizedSubClass.includes('swaption')) {
    return {
      toneName: 'swaption' as DerivativeTone,
      icon: Gauge,
      eyebrow: 'Options on Rates',
      title: 'Swaption',
      description: 'Stage option premium, strike, expiry, and the underlying fixed/floating swap terms together.',
      badges: ['Option terms', 'Underlying swap', 'Lifecycle ticket'],
      signals: [
        ...baseSignals,
        { label: 'Notional', value: formatDerivativeCurrency(notional, currency), accent: true },
        { label: 'Strike', value: formatPercentSignal(formData.strike) },
        { label: 'Expiry', value: firstValue(formData.expirationDate, '-') },
      ],
    };
  }

  if (normalizedSubClass.includes('futures')) {
    const contracts = Number(formData.contracts);
    return {
      toneName: 'futures' as DerivativeTone,
      icon: CalendarDays,
      eyebrow: 'Listed Derivatives',
      title: 'Futures',
      description: 'Trade listed futures with contract reference data, tick economics, margin inputs, fills, and lifecycle controls.',
      badges: ['Listed contract', 'Margin preview', 'Execution fills'],
      signals: [
        ...baseSignals,
        { label: 'Contracts', value: formatDerivativeNumber(contracts, 0), accent: true },
        { label: 'Price', value: firstValue(formData.tradePrice, '-') },
        { label: 'Expiry', value: firstValue(formData.expiryDate, '-') },
      ],
    };
  }

  if (normalizedSubClass.includes('options')) {
    const contracts = Number(formData.contracts);
    return {
      toneName: 'options' as DerivativeTone,
      icon: Sigma,
      eyebrow: 'Listed / OTC Options',
      title: 'Options',
      description: 'Capture option contract identity, strike, premium, expiry, pricing inputs, Greeks preview, and fills.',
      badges: ['Option contract', 'Greeks preview', 'Execution fills'],
      signals: [
        { label: 'Type / Direction', value: `${firstValue(formData.optionType, '-')} / ${directionLabel(formData.direction)}`, accent: true },
        { label: 'Contracts', value: formatDerivativeNumber(contracts, 0), accent: true },
        { label: 'Strike', value: firstValue(formData.strikePrice, '-') },
        { label: 'Premium', value: firstValue(formData.tradePrice, '-') },
      ],
    };
  }

  return {
    toneName: 'rates' as DerivativeTone,
    icon: CircleDollarSign,
    eyebrow: 'Derivatives',
    title: subClass || 'Derivative Ticket',
    description: 'Capture derivative economics, execution ownership, and lifecycle controls in one ticket.',
    badges: ['Trade economics', 'Execution workflow', 'Lifecycle ticket'],
    signals: [
      ...baseSignals,
      { label: 'Notional', value: formatDerivativeCurrency(notional, currency), accent: true },
      { label: 'Tenor', value: firstValue(formData.tenor, formData.swapTenor, '-') },
      { label: 'Currency', value: currency },
    ],
  };
};

export interface BaseTicketScreenProps {
  title: string;
  assetClass: string;
  assetSubClass?: string;
  hidePortfolioField?: boolean; // Option to hide the portfolio field if it's rendered in the form
  // A ticket that reconciles or validates the Allocation Grid itself (loan block, LL-FR-14) hears every change.
  onAllocationsChange?: (allocations: Array<{ portfolioId: string; portfolioName: string; allocatedAmount: string; notes: string }>, useAllocations: boolean) => void;
  suppressExecutionClearingSection?: boolean; // Let a ticket place the shared section manually.
  aiSuggestionsLabel?: string;
  aiSuggestionsLoadingLabel?: string;
  aiSuggestionsAssetClass?: string;
  aiSuggestionsPortfolioId?: number | null;
  aiSuggestionsSubMessage?: string;
  showAiSuggestions?: boolean;
  // An externally executed LL confirmation is controlled after the fact:
  // Compliance + Operations approve the exception, then DRAFT may Release.
  // It must never be submitted through retroactive preclear.
  lateBookingControl?: {
    active: boolean;
    fullyApproved: boolean;
  };
  children: (props: {
    formData: any;
    setFormData: React.Dispatch<React.SetStateAction<any>>;
    errors: { [key: string]: string };
    setErrors: (errors: { [key: string]: string } | ((prev: { [key: string]: string }) => { [key: string]: string })) => void;
    dimensionData: any;
    transactionStatus: 'DRAFT' | 'PRECLEAR_PENDING' | 'PRECLEAR_APPROVED' | 'PRECLEAR_REJECTED' | 'SENT' | 'EXECUTED' | 'POSTED' | 'CANCELLED' | 'VOIDED' | null;
    editingTransactionId: number | null;
    isLoading: boolean;
    isFieldDisabled: (field: string) => boolean;
    handleInputChange: (field: string, value: string) => void;
    handleInputBlur: (field: string, value: string) => void;
    validateForm: (action: string) => boolean;
    getButtonStates: () => any;
    useAllocations: boolean;
    allocations: Array<{ portfolioId: string; portfolioName: string; allocatedAmount: string; notes: string }>;
    setAllocations: React.Dispatch<React.SetStateAction<Array<{ portfolioId: string; portfolioName: string; allocatedAmount: string; notes: string }>>>;
    setUseAllocations: React.Dispatch<React.SetStateAction<boolean>>;
    // Household data for retail
    households: any[];
    selectedHousehold: string;
    setSelectedHousehold: React.Dispatch<React.SetStateAction<string>>;
    filteredPortfolios: any[];
  }) => ReactNode;
  initialFormData: any;
  validateForm: (formData: any, action: string, useAllocations?: boolean) => { [key: string]: string };
  buildTransactionData: (formData: any, dimensionData: any, useAllocations?: boolean) => Promise<any>;
  // May be sync (return form data) or async (return Promise<form data>) so a
  // ticket can fetch its asset-class-specific silo (e.g. portco debt facility
  // terms) before populating the form.
  loadTransactionData?: (transaction: any, dimensionData?: any) => any | Promise<any>;
  // Per-ticket lifecycle overrides (see useTicketWorkflow): e.g. the LL ticket
  // routes send/execute for a multi-fund BLOCK_PARENT to the block advance
  // endpoint instead of the generic /api/trade-transactions actions.
  workflowOverrides?: TicketWorkflowOverrides;
}

/** A breach value with its unit: percent by default, else a factor, basis points, years or dollars. */
const formatBreachValue = (value: number | string | undefined, unit?: string): string => {
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value ?? '');
  switch (unit) {
    case undefined:
    case null:
    case '%': return `${n.toFixed(2)}%`;
    case 'factor': return n.toFixed(0);
    case 'bps': return `${n.toFixed(0)} bps`;
    case 'years': return `${n.toFixed(2)} yrs`;
    case 'USD': return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    default: return `${n.toFixed(2)} ${unit}`;
  }
};

const BaseTicketScreen: React.FC<BaseTicketScreenProps> = ({
  title,
  assetClass,
  assetSubClass,
  hidePortfolioField = false,
  onAllocationsChange,
  suppressExecutionClearingSection = false,
  aiSuggestionsLabel = '🤖 Get AI Suggestions',
  aiSuggestionsLoadingLabel = '🔄 Getting...',
  aiSuggestionsAssetClass,
  aiSuggestionsPortfolioId,
  aiSuggestionsSubMessage = 'Analyzing portfolio and generating trade recommendations',
  showAiSuggestions = true,
  children,
  initialFormData,
  validateForm: validateFormProp,
  buildTransactionData,
  loadTransactionData,
  workflowOverrides,
  lateBookingControl
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [workflowState, workflowActions] = useTicketWorkflow(workflowOverrides);
  const [overrideJustification, setOverrideJustification] = useState('');
  const [overrideJustificationError, setOverrideJustificationError] = useState('');
  const { canDraft, canSend, canExecute, userRole } = useRole();
  const canSubmitPreClear = canSubmitForPreClear(userRole);
  const canApproveReject = canApproveRejectTrades(userRole);
  const executionClearingConfig = useMemo(
    () => getTicketExecutionClearingConfig(assetClass, assetSubClass),
    [assetClass, assetSubClass]
  );
  const [formData, setFormData] = useState({
    ...executionClearingInitialFields,
    ...initialFormData,
    ...(location.state?.ticketPrefill || {})
  });
  const [dimensionData, setDimensionData] = useState({
    assetManagers: [] as any[],
    portfolioManagers: [] as any[],
    assetClasses: [] as any[],
    assetSubClasses: [] as any[],
    portfolios: [] as any[],
    currencies: [] as any[],
    users: [] as any[]
  });
  const [allocations, setAllocations] = useState<Array<{portfolioId: string, portfolioName: string, allocatedAmount: string, notes: string}>>([]);
  const [useAllocations, setUseAllocations] = useState(false);
  useEffect(() => {
    if (onAllocationsChange) onAllocationsChange(allocations, useAllocations);
  }, [allocations, useAllocations]); // eslint-disable-line react-hooks/exhaustive-deps
  const [attachments, setAttachments] = useState<any[]>([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [generatingRationale, setGeneratingRationale] = useState(false);
  const [tradeSuggestions, setTradeSuggestions] = useState<any[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Section expander state
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    allocations: false,
    notes: false,
    rationale: false,
    attachments: false
  });

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Blocking operation overlay state
  const [blockingOperation, setBlockingOperation] = useState<{
    isActive: boolean;
    message: string;
    subMessage?: string;
  }>({ isActive: false, message: '' });

  // Audit history drawer state
  const [isAuditDrawerOpen, setIsAuditDrawerOpen] = useState(false);

  // ============================================================================
  // TENANT CONTEXT (Phase 2 - Institutional vs Retail)
  // ============================================================================
  const { isRetail } = useTenantConfig();

  // Household state for retail clients
  const [households, setHouseholds] = useState<any[]>([]);
  const [selectedHousehold, setSelectedHousehold] = useState<string>('');
  const [filteredPortfolios, setFilteredPortfolios] = useState<any[]>([]);
  const [selectedPortfolioTaxStatus, setSelectedPortfolioTaxStatus] = useState<string | null>(null);

  // Prospective portfolio impact state
  const [prospectiveImpact, setProspectiveImpact] = useState<ProspectiveState | null>(null);
  const [prospectiveLoading, setProspectiveLoading] = useState(false);

  // V284 origination block-trade portfolio assignment state.
  // loadedPortfolioId is the portfolio that the loaded trade ALREADY has
  // (null when the trade was originated from a doc as a portfolio-less block).
  const [loadedPortfolioId, setLoadedPortfolioId] = useState<number | null>(null);
  // A ticket booked by the Portfolio Builder carries its analysis in source_ref; the checker sees
  // whether the book it was sized against has moved (design §3.5 step 7).
  const [builderRun, setBuilderRun] = useState<{ runId: string; stale: boolean | null; detail: string; buyer?: string | null; custody?: string | null } | null>(null);
  // The stored pre-clear result (every rule that ran, with before/after/limit), shown once the
  // ticket has been through pre-clear so the checker sees the numbers, not just "approved".
  const [preClearRules, setPreClearRules] = useState<any[] | null>(null);
  const [preClearRulesOpen, setPreClearRulesOpen] = useState(false);
  useEffect(() => {
    const id = workflowState.editingTransactionId;
    const status = workflowState.transactionStatus || '';
    if (!id || !['PRECLEAR_PENDING', 'PRECLEAR_APPROVED', 'PRECLEAR_REJECTED', 'READY', 'SENT', 'EXECUTED', 'POSTED'].includes(status)) { setPreClearRules(null); return; }
    let cancelled = false;
    axios.get(`/api/pre-clear/${id}/status`).then(res => {
      if (cancelled) return;
      const notes = res.data?.complianceNotes;
      try {
        const parsed = typeof notes === 'string' ? JSON.parse(notes) : notes;
        const rules = parsed?.result?.rules_checked ?? parsed?.rules_checked;
        setPreClearRules(Array.isArray(rules) ? rules : null);
      } catch { setPreClearRules(null); }
    }).catch(() => { if (!cancelled) setPreClearRules(null); });
    return () => { cancelled = true; };
  }, [workflowState.editingTransactionId, workflowState.transactionStatus]);
  // The dates a loaded draft opened with, to tell an edit on the ticket from an untouched date.
  const [loadedDates, setLoadedDates] = useState<{ tradeDate?: string; settleDate?: string } | null>(null);

  // Fetch households for retail clients
  useEffect(() => {
    if (isRetail) {
      const fetchHouseholds = async () => {
        try {
          const response = await axios.get('/api/advisor/households/all');
          const householdData = response.data || [];
          setHouseholds(householdData);
        } catch (error) {
          console.error('Error fetching households:', error);
          setHouseholds([]);
        }
      };
      fetchHouseholds();
    }
  }, [isRetail]);

  // Filter portfolios by household when selected
  useEffect(() => {
    if (isRetail && selectedHousehold) {
      const householdPortfolios = dimensionData.portfolios.filter((p: any) =>
        p.householdId?.toString() === selectedHousehold
      );
      setFilteredPortfolios(householdPortfolios);
    } else {
      setFilteredPortfolios(dimensionData.portfolios);
    }
  }, [selectedHousehold, dimensionData.portfolios, isRetail]);

  // Check if selected portfolio is taxable (for tax warning)
  useEffect(() => {
    if (formData.portfolio && isRetail) {
      const selectedPortfolio = dimensionData.portfolios.find(
        (p: any) => p.portfolioName === formData.portfolio
      );
      setSelectedPortfolioTaxStatus(selectedPortfolio?.taxStatus || null);
    } else {
      setSelectedPortfolioTaxStatus(null);
    }
  }, [formData.portfolio, dimensionData.portfolios, isRetail]);

  // Load dimension data
  useEffect(() => {
    const fetchDimensionData = async () => {
      try {
        // Fetch each endpoint individually to handle errors gracefully
        const results = await Promise.allSettled([
          axios.get('/api/dimensions/asset-managers'),
          axios.get('/api/dimensions/portfolio-managers'),
          axios.get('/api/dimensions/asset-classes'),
          axios.get('/api/dimensions/portfolios'),
          axios.get('/api/dimensions/currencies'),
          axios.get('/api/dimensions/users')
        ]);
        
        // Helper to extract content from paginated response
        const extractContent = (data: any) => {
          if (!data) return [];
          // Check if it's a paginated response (has 'content' property)
          if (data.content && Array.isArray(data.content)) {
            return data.content;
          }
          // If it's already an array, return it
          if (Array.isArray(data)) {
            return data;
          }
          return [];
        };
        
        // Get portfolios from API
        const portfolios = results[3].status === 'fulfilled' ? extractContent(results[3].value.data) : [];

        setDimensionData({
          assetManagers: results[0].status === 'fulfilled' ? extractContent(results[0].value.data) : [],
          portfolioManagers: results[1].status === 'fulfilled' ? extractContent(results[1].value.data) : [],
          assetClasses: results[2].status === 'fulfilled' ? extractContent(results[2].value.data) : [],
          assetSubClasses: [],
          portfolios: portfolios,
          currencies: results[4].status === 'fulfilled' ? extractContent(results[4].value.data) : [],
          users: results[5].status === 'fulfilled' ? extractContent(results[5].value.data) : []
        });
        
        // Log any failures for debugging
        results.forEach((result, index) => {
          if (result.status === 'rejected') {
            const endpointNames = ['asset-managers', 'portfolio-managers', 'asset-classes', 'portfolios', 'currencies', 'users'];
            console.warn(`Failed to fetch ${endpointNames[index]}:`, result.reason);
          }
        });
      } catch (error) {
        console.error('Error fetching dimension data:', error);
      }
    };
    
    fetchDimensionData();
  }, []);

  // Fetch asset sub-classes when asset class changes
  useEffect(() => {
    if (formData.assetClass) {
      const fetchAssetSubClasses = async () => {
        try {
          // Some ticket kinds use an internal asset-class key (e.g. 'LEVERAGED_LOAN')
          // that differs from the DB name ('Fixed Income').  Map those aliases before lookup.
          const ASSET_CLASS_ALIASES: Record<string, string> = {
            'LEVERAGED_LOAN':  'Fixed Income',
            'LEVERAGED_LOANS': 'Fixed Income',
          };
          const resolvedAssetClass =
            ASSET_CLASS_ALIASES[(formData.assetClass || '').toUpperCase().replace(/[\s-]+/g, '_')]
            ?? formData.assetClass;
          let assetClassObj = dimensionData.assetClasses.find((ac: any) => ac.assetClassName === resolvedAssetClass);
          // Fallback: if asset classes haven't loaded yet, fetch them first
          if (!assetClassObj && dimensionData.assetClasses.length === 0) {
            try {
              const acResponse = await axios.get('/api/dimensions/asset-classes');
              const acList = acResponse.data?.content || acResponse.data || [];
              assetClassObj = acList.find((ac: any) => ac.assetClassName === resolvedAssetClass);
            } catch (e) { /* ignore */ }
          }
          if (assetClassObj) {
            const response = await axios.get(`/api/dimensions/asset-sub-classes/asset-class/${assetClassObj.id}`);
            // Extract content from paginated response if needed
            const extractContent = (data: any) => {
              if (!data) return [];
              if (data.content && Array.isArray(data.content)) {
                return data.content;
              }
              if (Array.isArray(data)) {
                return data;
              }
              return [];
            };
            setDimensionData((prev: any) => ({ ...prev, assetSubClasses: extractContent(response.data) }));
          }
        } catch (error) {
          console.error('Error fetching asset sub-classes:', error);
        }
      };
      fetchAssetSubClasses();
    } else {
      // Clear asset sub-classes when asset class is cleared
      setDimensionData((prev: any) => ({ ...prev, assetSubClasses: [] }));
    }
  }, [formData.assetClass, dimensionData.assetClasses, assetClass]);

  // Fetch prospective portfolio impact when key fields are set
  useEffect(() => {
    const portfolioId = formData.portfolioId || formData.portfolio;
    const securityId = formData.securityId || formData.security;
    const amount = parseAmountShorthand(formData.amount) || parseAmountShorthand(formData.quantity) || 0;
    const txType = formData.transactionType || formData.side || 'BUY';

    if (!portfolioId || !securityId || amount <= 0) {
      setProspectiveImpact(null);
      return;
    }

    const timer = setTimeout(async () => {
      setProspectiveLoading(true);
      try {
        const res = await axios.post('/api/compliance/validate-trade', {
          portfolioId: parseInt(portfolioId),
          securityId: parseInt(securityId),
          transactionType: txType,
          amount,
          price: parseFloat(formData.price) || 100,
          quantity: parseAmountShorthand(formData.quantity) || amount,
          tradeDate: formData.tradeDate || getTodayDate(),
          settleDate: formData.settleDate || formData.settlementDate || getTodayDate(),
        });
        // The validate-trade response may include prospective state
        if (res.data?.prospectiveState) {
          setProspectiveImpact(res.data.prospectiveState);
        }
      } catch {
        // Silently fail — prospective impact is supplementary
      }
      setProspectiveLoading(false);
    }, 800); // debounce

    return () => clearTimeout(timer);
  }, [formData.portfolioId, formData.portfolio, formData.securityId, formData.security,
      formData.amount, formData.quantity, formData.transactionType, formData.side,
      formData.price]);

  // Load transaction if editing
  useEffect(() => {
    const transactionId = location.state?.transactionId || location.state?.id;
    if (transactionId && workflowState.editingTransactionId !== transactionId) {
      const loadTransaction = async () => {
        try {
          const response = await axios.get(`/api/trade-transactions/${transactionId}`);
          const transaction = response.data;

          let sourceRef: string = transaction.sourceRef || '';
          if (!sourceRef.startsWith('builder-run:') && transaction.parentTransactionId) {
            try {
              const parent = await axios.get(`/api/trade-transactions/${transaction.parentTransactionId}`);
              if (String(parent.data?.sourceRef || '').startsWith('builder-run:')) sourceRef = parent.data.sourceRef;
            } catch { /* the parent is not readable; no banner */ }
          }
          if (sourceRef.startsWith('builder-run:')) {
            const runId = sourceRef.split(':')[1].split('#')[0];
            // The CLO that buys: stamped per allocation by the builder from the deal linked to the portfolio.
            const buyer: string | null = transaction.buyerLegalEntity || null;
            const custody: string | null = transaction.settlementInstruction || null;
            setBuilderRun({ runId, stale: null, detail: 'checking the book against the saved analysis…', buyer, custody });
            axios.get(`/api/portfolio-construction/clo/runs/${runId}/freshness`).then(fr => {
              const mine = (fr.data?.portfolios || []).find((p: any) => String(p.portfolioId) === String(transaction.portfolio?.id ?? transaction.portfolioId));
              setBuilderRun({ runId, stale: mine ? !!mine.stale : !!fr.data?.stale, detail: mine?.reason || (fr.data?.stale ? 'a book in the analysis changed' : 'unchanged'), buyer, custody });
            }).catch(() => setBuilderRun({ runId, stale: null, detail: 'freshness could not be checked', buyer, custody }));
          } else {
            setBuilderRun(null);
          }

          // Set workflow state
          workflowActions.setEditingTransactionId(transaction.id);
          workflowActions.setTransactionStatus(transaction.status || 'DRAFT');
          workflowActions.setIsEditMode(true);

          // V284: capture the trade's portfolio_id (null for doc-originated
          // block trades that haven't been assigned yet).
          const txPortfolioId = transaction.portfolio?.id
            ?? transaction.portfolioId
            ?? null;
          setLoadedPortfolioId(txPortfolioId);

          // Load form data if mapping function provided. Supports both sync
          // (return object) and async (return Promise) so tickets like portco
          // debt can fetch their silo before the form is populated.
          if (loadTransactionData) {
            const result = loadTransactionData(transaction, dimensionData);
            const mappedData = result && typeof (result as any).then === 'function'
              ? await result
              : result;
            setFormData({
              ...executionClearingInitialFields,
              ...mappedData,
              ...loadExecutionClearingFields(transaction)
            });
            setLoadedDates({ tradeDate: mappedData?.tradeDate, settleDate: mappedData?.settleDate });
          }
          
          // Load allocations
          if (transaction.allocations && transaction.allocations.length > 0) {
            setAllocations(transaction.allocations.map((alloc: any) => ({
              portfolioId: alloc.portfolioId?.toString() || '',
              portfolioName: alloc.portfolioName || '',
              allocatedAmount: alloc.allocatedAmount?.toString() || '',
              notes: alloc.notes || ''
            })));
            setUseAllocations(true);
          }
          
          // Load attachments
          fetchAttachments(transaction.id);
        } catch (error) {
          console.error('Error loading transaction:', error);
        }
      };
      
      loadTransaction();
    }
  }, [location.state?.transactionId, location.state?.id, workflowState.editingTransactionId, loadTransactionData, workflowActions]);

  // Restore the compliance sub-state on reload. PRECLEAR_PENDING is shared by
  // ordinary review and governed override review; compliance evidence tells
  // the ticket which message to show.
  useEffect(() => {
    const transactionId = workflowState.editingTransactionId;
    if (!transactionId || workflowState.transactionStatus !== 'PRECLEAR_PENDING') {
      workflowActions.setOverrideRequestPending(false);
      return;
    }
    axios.get(`/api/pre-clear/${transactionId}/status`)
      .then(response => {
        const notes = response.data?.complianceNotes;
        workflowActions.setOverrideRequestPending(
          typeof notes === 'string' && notes.includes('"mode":"OVERRIDE_REQUEST"')
        );
      })
      .catch(() => workflowActions.setOverrideRequestPending(false));
  }, [workflowState.editingTransactionId, workflowState.transactionStatus]);

  // Restore household selection when loading a transaction with a portfolio
  // This runs when both formData.portfolio and dimensionData.portfolios are available
  useEffect(() => {
    if (isRetail && formData.portfolio && dimensionData.portfolios.length > 0 && !selectedHousehold) {
      const portfolio = dimensionData.portfolios.find(
        (p: any) => p.portfolioName === formData.portfolio
      );
      if (portfolio?.householdId) {
        setSelectedHousehold(portfolio.householdId.toString());
      }
    }
  }, [isRetail, formData.portfolio, dimensionData.portfolios, selectedHousehold]);

  // Fetch attachments
  const fetchAttachments = async (transactionId: number) => {
    try {
      const response = await axios.get(`/api/trade-transactions/${transactionId}/attachments`);
      setAttachments(response.data || []);
    } catch (error) {
      console.error('Error fetching attachments:', error);
    }
  };
  
  // Handle file upload
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!workflowState.editingTransactionId) {
      workflowActions.setErrors({ submit: 'Please draft the transaction first before uploading attachments.' });
      return;
    }
    
    setUploadingFile(true);
    workflowActions.setErrors({});
    
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('uploadedBy', formData.submittedBy || 'System');
      
      const response = await axios.post(
        `/api/trade-transactions/${workflowState.editingTransactionId}/attachments`,
        uploadFormData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      
      setAttachments(prev => [...prev, response.data]);
      workflowActions.setShowSuccess(true);
      workflowActions.setSuccessMessage('File uploaded successfully!');
      setTimeout(() => {
        workflowActions.setShowSuccess(false);
        workflowActions.setSuccessMessage('');
      }, 3000);
    } catch (error: any) {
      console.error('Error uploading file:', error);
      workflowActions.setErrors({ submit: `Failed to upload file: ${error.response?.data || error.message}` });
    } finally {
      setUploadingFile(false);
      event.target.value = '';
    }
  };
  
  // Handle remove attachment
  const handleRemoveAttachment = async (attachmentId: number) => {
    try {
      await axios.delete(`/api/trade-transactions/attachments/${attachmentId}`);
      setAttachments(prev => prev.filter(att => att.id !== attachmentId));
      workflowActions.setShowSuccess(true);
      workflowActions.setSuccessMessage('Attachment removed successfully!');
      setTimeout(() => {
        workflowActions.setShowSuccess(false);
        workflowActions.setSuccessMessage('');
      }, 2000);
    } catch (error: any) {
      console.error('Error removing attachment:', error);
      workflowActions.setErrors({ submit: `Failed to remove attachment: ${error.response?.data || error.message}` });
    }
  };
  
  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };
  
  // Get file icon
  const getFileIcon = (fileName: string): string => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (['pdf'].includes(ext || '')) return '📄';
    if (['doc', 'docx'].includes(ext || '')) return '📝';
    if (['xls', 'xlsx'].includes(ext || '')) return '📊';
    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext || '')) return '🖼️';
    return '📎';
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev: any) => {
      const updated = { ...prev, [field]: value };
      return updated;
    });
    // Clear error for this field when user starts typing
    if (workflowState.errors[field]) {
      workflowActions.setErrors((prev: any) => ({ ...prev, [field]: '' }));
    }
  };

  const handleInputBlur = (field: string, value: string) => {
    // Validate individual field on blur - use current formData with the updated value
    const updatedFormData = { ...formData, [field]: value };
    const fieldErrors = validateFormProp(updatedFormData, 'draft', useAllocations);
    if (fieldErrors[field]) {
      workflowActions.setErrors((prev: any) => ({ ...prev, [field]: fieldErrors[field] }));
    } else if (workflowState.errors[field]) {
      // Clear error if field is now valid
      workflowActions.setErrors((prev: any) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const isFieldDisabled = (field: string): boolean => {
    const status = workflowState.transactionStatus;
    // Executed By should always be editable (except when EXECUTED or CANCELLED)
    // since it's required for execution and users may want to set it before releasing
    if (field === 'executedBy') {
      return status === 'EXECUTED' || status === 'CANCELLED';
    }
    if (!status || status === 'DRAFT') return false;
    // Execution & clearing details are finalized between release and execution
    // (operator finding 2026-08-05: they froze after DRAFT, so a trade that
    // reached SENT without a broker could never get one — deadlocking the new
    // execute-time requirement). Editable until the trade is EXECUTED;
    // persisted by the execute-time PATCH.
    if (EC_FIELD_KEYS.includes(field)
        && (status === 'PRECLEAR_PENDING' || status === 'PRECLEAR_APPROVED' || status === 'SENT')) {
      return false;
    }
    if (status === 'SENT') {
      // Only Price and Executed By are editable when SENT
      return field !== 'price' && field !== 'executedBy';
    }
    // EXECUTED, CANCELLED - all fields disabled
    return true;
  };

  const validateForm = (action: string): boolean => {
    // Central manager validation (operator finding 2026-08-05): FI/swap/FX/
    // equity tickets each required AM/PM in their own validators while
    // options/futures did not — a futures trade posted with neither. Enforce
    // once here for every ticket that carries the fields, on constructive
    // actions only (void/reject must never be blocked by missing fields).
    const constructiveAction = action === 'draft' || action === 'submitForPreClear'
      || action === 'send' || action === 'execute';
    const baseErrors: { [key: string]: string } = {};
    if (constructiveAction) {
      if ('assetManager' in formData && !String(formData.assetManager || '').trim()) {
        baseErrors.assetManager = 'Asset Manager is required';
      }
      if ('portfolioManager' in formData && !String(formData.portfolioManager || '').trim()) {
        baseErrors.portfolioManager = 'Portfolio Manager is required';
      }
    }
    const errors = {
      ...baseErrors,
      ...validateFormProp(formData, action, useAllocations),
      ...validateExecutionClearingFields(formData, executionClearingConfig, action)
    };
    if (Object.keys(errors).length > 0) {
      workflowActions.setErrors(errors);
      return false;
    }
    return true;
  };

  // V284 origination block-trade gate: a DRAFT trade with no portfolio AND
  // no allocations cannot be submitted for pre-clear. The user must pick a
  // portfolio in the form OR define allocations first. (The standalone
  // "Block Trade — Portfolio Assignment Required" banner was removed because
  // it duplicated the form's portfolio dropdown and forced single-portfolio
  // assignment, blocking the multi-fund allocations workflow.)
  const hasPortfolioInForm = !!(formData.portfolio || formData.portfolioId);
  const needsPortfolioAssignment =
    !hidePortfolioField  // if child hides the field, it manages portfolio itself — don't gate
    && workflowState.editingTransactionId !== null
    && workflowState.transactionStatus === 'DRAFT'
    && !loadedPortfolioId
    && !hasPortfolioInForm
    && !useAllocations
    && allocations.length === 0;

  const getButtonStates = () => {
    const status = workflowState.transactionStatus;
    const isDraft = status === 'DRAFT';
    const isPreClearPending = status === 'PRECLEAR_PENDING';
    const isPreClearApproved = status === 'PRECLEAR_APPROVED';
    const isPreClearRejected = status === 'PRECLEAR_REJECTED';
    const isSent = status === 'SENT';
    const isComplete = status === 'EXECUTED' || status === 'CANCELLED' || status === 'VOIDED';
    const hasTransaction = workflowState.editingTransactionId !== null;

    return {
      draft: !hasTransaction || (isDraft && !isComplete),
      submitForPreClear: !lateBookingControl?.active
        && isDraft && hasTransaction && !isComplete && !needsPortfolioAssignment,
      approvePreClear: isPreClearPending && !isComplete,
      rejectPreClear: isPreClearPending && !isComplete,
      send: (isPreClearApproved
        || (isDraft && lateBookingControl?.active && lateBookingControl.fullyApproved))
        && hasTransaction && !isComplete,
      execute: isSent && !isComplete,
      void: (isDraft || isPreClearRejected) && hasTransaction && !isComplete,
      reject: isSent && !isComplete,
      // Posted economics are immutable — reversal (linked offsetting trade)
      // is the only undo. Mirrors Void/Reject: one shared button, POSTED only.
      reverse: status === 'POSTED' && hasTransaction,
      duplicate: hasTransaction && !isComplete
    };
  };

  const handleDuplicateTicket = () => {
    // Strip the deep-link id from router history BEFORE resetting: the
    // load-transaction effect re-fires when editingTransactionId changes and
    // would immediately re-fetch the original trade, snapping the ticket
    // back to its loaded state (operator finding 2026-08-04: Duplicate
    // "does nothing").
    navigate(location.pathname, { replace: true, state: {} });
    workflowActions.handleDuplicate(formData);
  };

  const handleReverse = async () => {
    const tradeId = workflowState.editingTransactionId;
    if (!tradeId) return;
    const reason = window.prompt(
      `Reverse trade #${tradeId}?\n\nThis removes its position and cash economics as-of today ` +
      `(history stays replayable) via a linked reversal trade, and cannot itself be undone ` +
      `except by rebooking.\n\nReason (optional):`);
    if (reason === null) return;
    setBlockingOperation({ isActive: true, message: 'Reversing Trade...', subMessage: 'Posting offsetting economics' });
    try {
      const res = await axios.post(`/api/trade-transactions/${tradeId}/reverse`, { reason });
      alert(`✅ Trade #${tradeId} reversed (reversal trade #${res.data?.reversalTradeId})`);
      navigate('/bond-transactions');
    } catch (error: any) {
      alert(`❌ Reversal failed: ${error.response?.data?.error || error.message}`);
    } finally {
      setBlockingOperation({ isActive: false, message: '', subMessage: '' });
    }
  };

  const handleSubmit = async (action: 'draft' | 'submitForPreClear' | 'approvePreClear' | 'rejectPreClear' | 'send' | 'execute' | 'void' | 'reject') => {
    // The late-booked confirm was already externally executed. Release is a
    // lifecycle transition after dual approvals, not an edit/resubmission of
    // the document-derived economics, so ordinary ticket-field validation is
    // intentionally not re-run here. The backend independently enforces both
    // approval timestamps before DRAFT can transition to SENT.
    const approvedLateBookingRelease = action === 'send'
      && workflowState.transactionStatus === 'DRAFT'
      && lateBookingControl?.active
      && lateBookingControl.fullyApproved;
    if (!approvedLateBookingRelease && !validateForm(action)) {
      return;
    }

    // Validate allocations if using allocation grid
    if (useAllocations) {
      const validAllocations = allocations.filter(alloc => {
        const portfolioIdNum = parseInt(alloc.portfolioId);
        return !isNaN(portfolioIdNum) && portfolioIdNum > 0 && alloc.allocatedAmount && alloc.allocatedAmount.trim() !== '';
      });
      if (validAllocations.length === 0) {
        workflowActions.setErrors({ submit: 'At least one allocation with portfolio and amount is required' });
        return;
      }
    }

    // Show blocking overlay based on action
    const actionMessages: Record<string, { message: string; subMessage: string }> = {
      draft: { message: 'Saving Draft...', subMessage: 'Validating and saving your trade' },
      send: { message: 'Releasing Trade...', subMessage: 'Releasing for execution' },
      execute: { message: 'Executing Trade...', subMessage: 'Processing execution' },
      void: { message: 'Voiding Trade...', subMessage: 'Cancelling the trade' },
      reject: { message: 'Rejecting Trade...', subMessage: 'Marking trade as rejected' }
    };
    
    setBlockingOperation({
      isActive: true,
      message: actionMessages[action]?.message || 'Processing...',
      subMessage: actionMessages[action]?.subMessage
    });

    try {
      // Only build a fresh transaction payload for 'draft' — that is the only
      // action that actually creates a trade. All other actions
      // (submitForPreClear, approvePreClear, rejectPreClear, send, execute,
      // void, reject) flip status on an EXISTING trade via editingTransactionId
      // and do not consume transactionData.
      //
      // This matters because some tickets give buildTransactionData side
      // effects (e.g. RealEstateTicketScreen calls /api/credit/re-debt/prepare,
      // which writes a real_estate_loan + dim_security_master row). Calling it
      // on every status-transition click previously created 4-5 orphan
      // silo+SMF rows per lifecycle.
      let transactionData: any = null;

      if (action === 'draft') {
        transactionData = await buildTransactionData(formData, dimensionData, useAllocations);
        transactionData = {
          ...transactionData,
          ...buildExecutionClearingPayload(formData)
        };

        // Add allocations if using allocation mode
        if (useAllocations && allocations.length > 0) {
          transactionData.allocations = allocations
            .filter(alloc => alloc.portfolioId && alloc.allocatedAmount)
            .map(alloc => ({
              portfolioId: parseInt(alloc.portfolioId),
              allocatedAmount: parseAmountShorthand(alloc.allocatedAmount),
              notes: alloc.notes || ''
            }));
        }
      }

      // Check compliance before submitting (only for draft — send re-uses the
      // already-validated draft payload via editingTransactionId).
      if (action === 'draft' && transactionData) {
        try {
          // Use AI-powered validation
          const aiValidationResponse = await axios.post(
            '/api/ai/validate-trade',
            transactionData
          );
          
          const ruleBasedCompliance = aiValidationResponse.data.ruleBasedCompliance || {};
          const aiValidation = aiValidationResponse.data.aiValidation || {};
          
          const errors = ruleBasedCompliance.errors || [];
          const warnings = ruleBasedCompliance.warnings || [];
          const aiConcerns = aiValidation.concerns || [];
          const aiRecommendations = aiValidation.recommendations || [];
          const aiAssessment = aiValidation.assessment || "APPROVE";
          
          // Combine rule-based and AI validation.
          //
          // DRAFT creation must not be hard-blocked by compliance breaches. The
          // persisted DRAFT is the audit object that later routes through
          // pre-clear and, when applicable, the governed override workflow. If
          // this preview check blocks before persistence, a PM cannot request an
          // override and Compliance has no trade to approve/reject.
          if (!ruleBasedCompliance.compliant) {
            const allErrors = [...errors];
            
            if (allErrors.length > 0) {
              console.warn(
                'Draft compliance preview found blocking breaches; saving draft so submit-preclear can route governed override:',
                allErrors
              );
            }
          }

          // Handle AI Assessment separately - Demote REJECT to a Warning for flexibility
          if (aiAssessment === "REJECT") {
            const aiWarning = "AI Validation Warning: " + (aiValidation.summary || "Trade rejected by AI");
            warnings.push(aiWarning);
            
            // Log it for audit but don't block
            console.warn("AI rejected trade, but allowing as warning:", aiValidation.summary);
          }
          
          // Show warnings and AI feedback
          const allWarnings = [...warnings];
          if (aiConcerns.length > 0) {
            allWarnings.push(...aiConcerns.map((c: string) => "AI: " + c));
          }
          
          if (allWarnings.length > 0 || aiRecommendations.length > 0) {
            let message = "";
            if (allWarnings.length > 0) {
              message += "Warnings:\n" + allWarnings.join('\n') + "\n\n";
            }
            if (aiRecommendations.length > 0) {
              message += "AI Recommendations:\n" + aiRecommendations.join('\n') + "\n\n";
            }
            if (aiValidation.summary) {
              message += "AI Assessment: " + aiValidation.summary + "\n\n";
            }
            message += "Do you want to proceed anyway?";
            
            const proceed = window.confirm(message);
            if (!proceed) {
              return;
            }
          }
        } catch (complianceError) {
          console.warn('Validation check failed, proceeding anyway:', complianceError);
          // Don't block submission if validation check fails
        }
      }
      
      // Add allocations if using allocation grid (only relevant when we have
      // a fresh draft payload to attach them to).
      if (action === 'draft' && transactionData && useAllocations && allocations.length > 0) {
        const mappedAllocations = allocations
          .filter(alloc => {
            const portfolioIdNum = parseInt(alloc.portfolioId);
            return !isNaN(portfolioIdNum) && portfolioIdNum > 0 && alloc.allocatedAmount && alloc.allocatedAmount.trim() !== '';
          })
          .map(alloc => {
            const portfolioIdNum = parseInt(alloc.portfolioId);
            if (isNaN(portfolioIdNum) || portfolioIdNum <= 0) {
              throw new Error(`Invalid portfolio ID in allocation: ${alloc.portfolioId}`);
            }
            return {
              portfolioId: portfolioIdNum,
              allocatedAmount: parseAmountShorthand(alloc.allocatedAmount),
              notes: alloc.notes || ''
            };
          });

        if (mappedAllocations.length > 0) {
          transactionData.allocations = mappedAllocations;
          console.log('Sending allocations:', mappedAllocations);
        } else {
          console.warn('useAllocations is true but no valid allocations found');
          throw new Error('At least one valid allocation is required when using allocations');
        }
      }

      // Manager selections are part of the draft's audit context. Resolve the
      // displayed names to canonical dimension IDs before the first save so a
      // reopened ticket cannot silently lose them.
      if (action === 'draft' && transactionData) {
        if (formData.assetManager !== undefined) {
          const manager = dimensionData.assetManagers.find(
            (row: any) => row.assetManagerName === String(formData.assetManager || '').trim()
          );
          if (manager?.id) transactionData.assetManagerId = manager.id;
        }
        if (formData.portfolioManager !== undefined) {
          const manager = dimensionData.portfolioManagers.find(
            (row: any) => row.portfolioManagerName === String(formData.portfolioManager || '').trim()
          );
          if (manager?.id) transactionData.portfolioManagerId = manager.id;
        }
      }

      if (transactionData) {
        console.log('Transaction data being sent:', {
          ...transactionData,
          portfolioId: transactionData.portfolioId,
          allocationsCount: transactionData.allocations?.length || 0,
          allocations: transactionData.allocations
        });
      }

      if (action === 'draft') {
        const stored = await workflowActions.handleDraft(transactionData);
        // Show the settle date the book holds, which the server may have rolled off a holiday.
        if (stored?.settleDate && stored.settleDate !== formData.settleDate) {
          setFormData((prev: any) => ({ ...prev, settleDate: stored.settleDate }));
        }
        // Fetch attachments after creating transaction
        if (workflowState.editingTransactionId) {
          fetchAttachments(workflowState.editingTransactionId);
        }
      } else if (action === 'submitForPreClear' && workflowState.editingTransactionId) {
        // Persist user-edited Asset Manager / Portfolio Manager picks BEFORE
        // flipping status. submitForPreClear by itself only changes status —
        // without this PATCH the picked names get discarded on reload and the
        // dropdowns visually revert to option[0]. Resolves names → IDs from
        // dimensionData; only sends keys whose values resolve.
        try {
          const patch: Record<string, number | null> = {};
          if (formData.assetManager !== undefined) {
            const am = dimensionData.assetManagers.find(
              (a: any) => a.assetManagerName === (formData.assetManager || '').trim()
            );
            if (am?.id) patch.assetManagerId = am.id;
          }
          if (formData.portfolioManager !== undefined) {
            const pm = dimensionData.portfolioManagers.find(
              (p: any) => p.portfolioManagerName === (formData.portfolioManager || '').trim()
            );
            if (pm?.id) patch.portfolioManagerId = pm.id;
          }
          // Portfolio pick on V284 doc-ingested originations: trade was imported
          // portfolio-OPTIONAL (portfolio_id NULL). PM picks one in the pre-clear
          // form. Persist that here so submit-for-pre-clear doesn't bounce with
          // "Cannot submit without a portfolio or allocations."
          if (formData.portfolioId) {
            const pid = Number(formData.portfolioId);
            if (Number.isFinite(pid) && pid > 0) patch.portfolioId = pid;
          } else if (formData.portfolio) {
            const p = dimensionData.portfolios.find(
              (x: any) => x.portfolioName === (formData.portfolio || '').trim()
            );
            if (p?.id) patch.portfolioId = p.id;
          }
          if (Object.keys(patch).length > 0) {
            await axios.patch(
              `/api/trade-transactions/${workflowState.editingTransactionId}/managers`,
              patch
            );
          }
        } catch (mgrErr) {
          console.warn('Failed to persist manager picks before pre-clear:', mgrErr);
          // Don't block pre-clear submit on this — surface a warning instead
        }
        // Dates edited on a loaded draft: submit only changes status, so save them first. A date the
        // book refuses stops the submit, so the ticket never goes forward on dates the user did not see.
        if (loadedDates) {
          const dates: Record<string, string> = {};
          if (formData.tradeDate && formData.tradeDate !== loadedDates.tradeDate) dates.tradeDate = formData.tradeDate;
          if (formData.settleDate && formData.settleDate !== loadedDates.settleDate) dates.settleDate = formData.settleDate;
          if (Object.keys(dates).length > 0) {
            const saved = await axios.patch(
              `/api/trade-transactions/${workflowState.editingTransactionId}/managers`, dates);
            const stored = { tradeDate: saved.data?.tradeDate || formData.tradeDate, settleDate: saved.data?.settleDate || formData.settleDate };
            setLoadedDates(stored);
            // Show the dates the book holds; a settle date on a holiday rolls forward.
            setFormData((prev: any) => ({ ...prev, ...stored }));
          }
        }
        await workflowActions.handleSubmitForPreClear(workflowState.editingTransactionId);
      } else if (action === 'approvePreClear' && workflowState.editingTransactionId) {
        const notes = window.prompt('Enter approval notes (optional):');
        await workflowActions.handleApprovePreClear(workflowState.editingTransactionId, notes || undefined);
      } else if (action === 'rejectPreClear' && workflowState.editingTransactionId) {
        const rejectionReason = window.prompt('Enter rejection reason (required):');
        if (!rejectionReason || rejectionReason.trim() === '') {
          alert('Rejection reason is required');
          return;
        }
        await workflowActions.handleRejectPreClear(workflowState.editingTransactionId, rejectionReason);
      } else if (action === 'send' && workflowState.editingTransactionId) {
        await workflowActions.handleSend(workflowState.editingTransactionId);
      } else if (action === 'execute' && workflowState.editingTransactionId) {
        // Persist execution & clearing details BEFORE executing — the execute
        // call itself carries only price + executedBy, and these fields are
        // editable up to execution (operator finding 2026-08-05).
        if (executionClearingConfig.enabled) {
          try {
            await axios.patch(
              `/api/trade-transactions/${workflowState.editingTransactionId}/execution-clearing`,
              buildExecutionClearingPayload(formData)
            );
          } catch (ecErr: any) {
            // Fail loudly: executing while the broker/settlement details did
            // not persist would report success on a half-recorded execution.
            workflowActions.setErrors({
              submit: 'Could not save execution & clearing details — execution aborted: '
                + (ecErr?.response?.data?.error || ecErr?.message || 'unknown error')
            });
            setBlockingOperation({ isActive: false, message: '' });
            return;
          }
        }
        await workflowActions.handleExecute(workflowState.editingTransactionId, {
          price: formData.price,
          executedBy: formData.executedBy
        });
      } else if (action === 'void' && workflowState.editingTransactionId) {
        await workflowActions.handleVoid(workflowState.editingTransactionId);
      } else if (action === 'reject' && workflowState.editingTransactionId) {
        await workflowActions.handleReject(workflowState.editingTransactionId);
      }
    } catch (error: any) {
      console.error(`Error ${action}ing transaction:`, error);
      console.error(`[TRADE-ERROR] Action=${action}, Message=${error?.message}, Response=${JSON.stringify(error?.response?.data || {})}`);
      // F-REPO-019 (repo E2E 2026-08-27): a buildTransactionData failure (e.g.
      // a prepare-endpoint refusal like the collateral double-pledge guard)
      // was console-only — the operator clicked Save Draft and NOTHING
      // happened. Surface it through the same submit-error banner the
      // execution-clearing path uses.
      workflowActions.setErrors({
        submit: (error?.response?.data?.error || error?.response?.data?.message
          || error?.message || 'Unknown error')
      });
    } finally {
      // Clear blocking overlay
      setBlockingOperation({ isActive: false, message: '' });
    }
  };

  const submitComplianceOverrideRequest = async () => {
    const justification = overrideJustification.trim();
    if (justification.length < 10) {
      setOverrideJustificationError('Provide a specific business justification (at least 10 characters).');
      return;
    }
    if (!workflowState.editingTransactionId) {
      setOverrideJustificationError('Save the trade before requesting an override.');
      return;
    }
    setOverrideJustificationError('');
    const submitted = await workflowActions.handleRequestComplianceOverride(
      workflowState.editingTransactionId,
      justification
    );
    if (submitted) {
      workflowActions.clearPreClearBlock();
      setOverrideJustification('');
      setOverrideJustificationError('');
    } else {
      setOverrideJustificationError(
        'Unable to submit the override request. Please review the message above and try again.'
      );
    }
  };

  const buttonStates = getButtonStates();
  const shortcutContextRef = React.useRef<any>(null);
  const shortcutFeedbackTimerRef = React.useRef<number | null>(null);
  const [shortcutFeedback, setShortcutFeedback] = useState<{ message: string; tone: 'success' | 'warning' } | null>(null);

  useEffect(() => {
    shortcutContextRef.current = {
      buttonStates,
      canApproveReject,
      canDraft,
      canExecute,
      canSend,
      canSubmitPreClear,
      formData,
      handleSubmit,
      needsPortfolioAssignment,
      workflowActions,
      workflowState,
      navigate,
    };
  });

  useEffect(() => {
    const focusFirstError = (errors: { [key: string]: string } = {}) => {
      const firstErrorKey = Object.keys(errors)
        .find(key => key !== 'submit' && errors[key]);
      const field = firstErrorKey
        ? document.getElementById(firstErrorKey)
        : document.querySelector('.error');

      if (field instanceof HTMLElement) {
        field.scrollIntoView({ behavior: 'smooth', block: 'center' });
        field.focus?.();
      }
    };

    const showShortcutFeedback = (message: string, tone: 'success' | 'warning' = 'success') => {
      setShortcutFeedback({ message, tone });
      if (shortcutFeedbackTimerRef.current !== null) {
        window.clearTimeout(shortcutFeedbackTimerRef.current);
      }
      shortcutFeedbackTimerRef.current = window.setTimeout(() => {
        setShortcutFeedback(null);
        shortcutFeedbackTimerRef.current = null;
      }, 2600);
    };

    const describeBlockedShortcut = (
      action: string,
      status: string | null,
      hasTransaction: boolean,
      currentNeedsPortfolioAssignment: boolean
    ) => {
      if (action === 'submitForPreClear' && !hasTransaction) {
        return 'Shortcut received: save the ticket as a draft before submitting for pre-clear.';
      }
      if (action === 'submitForPreClear' && currentNeedsPortfolioAssignment) {
        return 'Shortcut received: assign a portfolio or allocations before submitting for pre-clear.';
      }
      if (action === 'approvePreClear') {
        return status === 'PRECLEAR_PENDING'
          ? 'Shortcut received: your current role cannot approve this pre-clear.'
          : 'Shortcut received: this ticket must be pre-clear pending before approval.';
      }
      if (action === 'send') {
        return 'Shortcut received: this ticket must be pre-clear approved before release.';
      }
      if (action === 'execute') {
        return 'Shortcut received: this ticket must be released before execution.';
      }
      if (action === 'duplicate') {
        return 'Shortcut received: save the ticket before duplicating it.';
      }
      if (action === 'draft') {
        return 'Shortcut received: the draft action is not available for the current ticket status.';
      }
      return 'Shortcut received, but that action is not available for the current ticket status or role.';
    };

    const runShortcutAction = (action: string) => {
      const ctx = shortcutContextRef.current;
      if (!ctx || ctx.workflowState.isLoading) return;

      const {
        buttonStates: currentButtonStates,
        canApproveReject: canApproveRejectCurrent,
        canDraft: canDraftCurrent,
        canExecute: canExecuteCurrent,
        canSend: canSendCurrent,
        canSubmitPreClear: canSubmitPreClearCurrent,
        formData: currentFormData,
        handleSubmit: currentHandleSubmit,
        needsPortfolioAssignment: currentNeedsPortfolioAssignment,
        workflowActions: currentWorkflowActions,
        workflowState: currentWorkflowState,
        navigate: currentNavigate,
      } = ctx;
      const status = currentWorkflowState.transactionStatus;
      const hasTransaction = currentWorkflowState.editingTransactionId !== null;

      const blockShortcut = () => {
        showShortcutFeedback(
          describeBlockedShortcut(action, status, hasTransaction, currentNeedsPortfolioAssignment),
          'warning'
        );
      };

      if (action === 'jumpErrors') {
        if (Object.values(currentWorkflowState.errors || {}).some(Boolean)) {
          focusFirstError(currentWorkflowState.errors);
          showShortcutFeedback('Shortcut received: moved to the first validation issue.');
        } else {
          showShortcutFeedback('Shortcut received: no validation issues are currently shown.');
        }
        return;
      }

      if (action === 'toggleAllocations') {
        setExpandedSections(prev => ({ ...prev, allocations: !prev.allocations }));
        showShortcutFeedback('Shortcut received: toggled allocations.');
        return;
      }

      if (action === 'duplicate') {
        if (currentButtonStates.duplicate) {
          // Same state-strip as the Duplicate button — without it the
          // load-transaction effect re-fetches the original immediately.
          navigate(location.pathname, { replace: true, state: {} });
          currentWorkflowActions.handleDuplicate(currentFormData);
        } else {
          blockShortcut();
        }
        return;
      }

      if (action === 'primary') {
        if (!currentWorkflowState.editingTransactionId && canDraftCurrent && currentButtonStates.draft) {
          currentHandleSubmit('draft');
        } else if (canSubmitPreClearCurrent && currentButtonStates.submitForPreClear) {
          currentHandleSubmit('submitForPreClear');
        } else if (canApproveRejectCurrent && currentButtonStates.approvePreClear) {
          currentHandleSubmit('approvePreClear');
        } else if (canSendCurrent && currentButtonStates.send) {
          currentHandleSubmit('send');
        } else if (canExecuteCurrent && currentButtonStates.execute) {
          currentHandleSubmit('execute');
        } else if (canDraftCurrent && currentButtonStates.draft) {
          currentHandleSubmit('draft');
        } else {
          blockShortcut();
        }
        return;
      }

      if (action === 'draft' && canDraftCurrent && currentButtonStates.draft) {
        currentHandleSubmit('draft');
      } else if (action === 'submitForPreClear' && canSubmitPreClearCurrent && currentButtonStates.submitForPreClear) {
        currentHandleSubmit('submitForPreClear');
      } else if (action === 'approvePreClear' && canApproveRejectCurrent && currentButtonStates.approvePreClear) {
        currentHandleSubmit('approvePreClear');
      } else if (action === 'send' && canSendCurrent && currentButtonStates.send) {
        currentHandleSubmit('send');
      } else if (action === 'execute' && canExecuteCurrent && currentButtonStates.execute) {
        currentHandleSubmit('execute');
      } else if (action === 'openPostingQueue') {
        currentNavigate('/bond-transactions?status=EXECUTED');
      } else {
        blockShortcut();
      }
    };

    const handleTicketShortcut = (event: Event) => {
      const action = (event as CustomEvent<{ action?: string }>).detail?.action;
      if (action) {
        runShortcutAction(action);
      }
    };

    const triggerShortcut = (event: KeyboardEvent, action: string) => {
      event.preventDefault();
      event.stopPropagation();
      runShortcutAction(action);
    };

    const handleTicketKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest?.('[role="dialog"]')) return;

      // Synthetic keydowns (browser autofill / extensions) can carry no
      // key/code — guard (F-CCS walkthrough finding 2026-08-07).
      if (!event.key || !event.code) return;
      const key = event.key.toLowerCase();
      const codeKey = event.code.startsWith('Key') ? event.code.slice(3).toLowerCase() : event.code.toLowerCase();
      const isKey = (letter: string) => key === letter || codeKey === letter;
      const commandModifier = event.metaKey || event.ctrlKey;
      const altShiftModifier = event.altKey && event.shiftKey && !event.metaKey && !event.ctrlKey;

      if (commandModifier && isKey('s')) {
        triggerShortcut(event, 'draft');
        return;
      }
      if (commandModifier && event.key === 'Enter') {
        triggerShortcut(event, 'primary');
        return;
      }
      if ((commandModifier && isKey('d') && !event.shiftKey) || (altShiftModifier && isKey('d'))) {
        triggerShortcut(event, 'duplicate');
        return;
      }
      if ((commandModifier && isKey('j') && !event.shiftKey) || (altShiftModifier && isKey('j'))) {
        triggerShortcut(event, 'jumpErrors');
        return;
      }
      if ((commandModifier && isKey('l') && !event.shiftKey) || (altShiftModifier && isKey('l'))) {
        triggerShortcut(event, 'toggleAllocations');
        return;
      }
      if ((commandModifier && event.shiftKey && isKey('p')) || (altShiftModifier && isKey('p'))) {
        triggerShortcut(event, 'submitForPreClear');
        return;
      }
      if ((commandModifier && event.shiftKey && isKey('a')) || (altShiftModifier && isKey('a'))) {
        triggerShortcut(event, 'approvePreClear');
        return;
      }
      if ((commandModifier && event.shiftKey && isKey('r')) || (altShiftModifier && isKey('r'))) {
        triggerShortcut(event, 'send');
        return;
      }
      if ((commandModifier && event.shiftKey && isKey('e')) || (altShiftModifier && isKey('e'))) {
        triggerShortcut(event, 'execute');
        return;
      }
      if ((commandModifier && event.shiftKey && isKey('o')) || (altShiftModifier && isKey('o'))) {
        triggerShortcut(event, 'openPostingQueue');
      }
    };

    window.addEventListener('axiom:ticket-shortcut', handleTicketShortcut);
    window.addEventListener('keydown', handleTicketKeyDown, true);
    return () => {
      window.removeEventListener('axiom:ticket-shortcut', handleTicketShortcut);
      window.removeEventListener('keydown', handleTicketKeyDown, true);
      if (shortcutFeedbackTimerRef.current !== null) {
        window.clearTimeout(shortcutFeedbackTimerRef.current);
      }
    };
  }, []);

  const derivativeHeroConfig = assetClass === 'Derivatives'
    ? buildDerivativeHeroConfig(assetSubClass, formData)
    : null;

  return (
    <Container>
      {/* Blocking overlay for long-running operations */}
      <LoadingOverlay
        isVisible={blockingOperation.isActive}
        message={blockingOperation.message}
        subMessage={blockingOperation.subMessage}
        showHourglass
      />

      {/* Navigation removed - now handled by InstitutionalTradeEntry/RetailTradeEntry */}
      <ContentWrapper>
        <MainContent>
          <PageTitle>{title}</PageTitle>

          {/* Household Filter removed - now integrated into the form below */}

          {/* Tax Warning for Taxable Accounts */}
          {isRetail && selectedPortfolioTaxStatus === 'TAXABLE' && (
            <TaxWarningBanner>
              <span>Warning: This is a taxable account. Consider tax implications before trading.</span>
            </TaxWarningBanner>
          )}

        <TicketHeader>
          <TicketInfo>
            <TicketLabel>TICKET NUMBER</TicketLabel>
            {workflowState.editingTransactionId ? (
              <TicketNumber
                onClick={() => setIsAuditDrawerOpen(true)}
                style={{ cursor: 'pointer' }}
                title="Click to view audit history"
              >
                🎫 #{workflowState.editingTransactionId}
              </TicketNumber>
            ) : (
              <TicketNumber>New Ticket</TicketNumber>
            )}
          </TicketInfo>
          
          {/* AI Suggestions Button - Only show for new tickets */}
          {showAiSuggestions && !workflowState.editingTransactionId && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <SecondaryButton
                type="button"
                onClick={async () => {
                  // Try to get portfolio from formData or allocations
                  let portfolioId: number | null = aiSuggestionsPortfolioId || null;
                  
                  if (!portfolioId && formData.portfolio) {
                    portfolioId = dimensionData.portfolios.find((p: any) => p.portfolioName === formData.portfolio)?.id;
                  } else if (useAllocations && allocations.length > 0) {
                    // Get portfolio from first allocation
                    const firstAlloc = allocations.find(a => a.portfolioId && a.portfolioId.trim() !== '');
                    if (firstAlloc) {
                      portfolioId = parseInt(firstAlloc.portfolioId);
                    }
                  }
                  
                  // If no portfolio found, show portfolio selector
                  if (!portfolioId) {
                    if (dimensionData.portfolios.length === 0) {
                      alert('No portfolios available. Please ensure portfolios are configured.');
                      return;
                    }
                    
                    const selectedPortfolio = prompt(
                      `Please select a portfolio:\n\n${dimensionData.portfolios.map((p: any, idx: number) => `${idx + 1}. ${p.portfolioName}`).join('\n')}\n\nEnter portfolio number (1-${dimensionData.portfolios.length}):`
                    );
                    
                    if (!selectedPortfolio) return;
                    
                    const portfolioIndex = parseInt(selectedPortfolio) - 1;
                    if (portfolioIndex >= 0 && portfolioIndex < dimensionData.portfolios.length) {
                      portfolioId = dimensionData.portfolios[portfolioIndex].id;
                    } else {
                      alert('Invalid portfolio selection');
                      return;
                    }
                  }
                  
                  setLoadingSuggestions(true);
                  setShowSuggestions(true);
                  setTradeSuggestions([]); // Clear previous suggestions
                  setBlockingOperation({
                    isActive: true,
                    message: 'Getting AI Suggestions...',
                    subMessage: aiSuggestionsSubMessage
                  });

                  try {
                    console.log('Requesting AI suggestions for portfolio:', portfolioId);
                    const suggestionAssetClass = aiSuggestionsAssetClass || formData.assetClass || assetClass || null;
                    console.log('Asset class being sent:', suggestionAssetClass);
                    
                    const response = await axios.post('/api/ai/trade-suggestions', {
                      portfolioId: portfolioId,
                      assetClass: suggestionAssetClass
                    }, {
                      timeout: 60000 // 60 second timeout
                    });
                    
                    console.log('AI suggestions response:', response.data);
                    
                    if (response.data && response.data.success) {
                      const suggestions = response.data.suggestions || [];
                      console.log('Received suggestions:', suggestions);
                      setTradeSuggestions(suggestions);
                      
                      if (suggestions.length === 0) {
                        alert('No suggestions generated. The AI may need more portfolio context.');
                      }
                    } else {
                      console.error('Unexpected response format:', response.data);
                      alert('Failed to get suggestions: ' + (response.data?.error || 'Unexpected response format'));
                      setShowSuggestions(false);
                    }
                  } catch (error: any) {
                    console.error('Error getting suggestions:', error);
                    const errorMessage = error.response?.data?.error || 
                                        error.message || 
                                        (error.code === 'ECONNABORTED' ? 'Request timed out. Please try again.' : 'Unknown error');
                    alert('Error getting suggestions: ' + errorMessage);
                    setShowSuggestions(false);
                  } finally {
                    setLoadingSuggestions(false);
                    setBlockingOperation({ isActive: false, message: '' });
                  }
                }}
                disabled={loadingSuggestions}
              >
                {loadingSuggestions ? aiSuggestionsLoadingLabel : aiSuggestionsLabel}
              </SecondaryButton>
            </div>
          )}
          
          <TicketStatusWrapper>
            <TicketLabel>Status</TicketLabel>
            <StatusValue status={workflowState.transactionStatus || 'Pre-Draft'}>
              {workflowState.transactionStatus 
                ? workflowState.transactionStatus.replace(/_/g, ' ')
                : 'Pre-Draft'}
            </StatusValue>
          </TicketStatusWrapper>
        </TicketHeader>

        {builderRun && (
          <div style={{
            margin: '0 0 12px', padding: '10px 14px', borderRadius: 10, fontSize: 12,
            border: `1px solid ${builderRun.stale ? 'rgba(245,158,11,0.4)' : 'rgba(99,102,241,0.35)'}`,
            background: builderRun.stale ? 'rgba(245,158,11,0.08)' : 'rgba(99,102,241,0.08)',
            color: builderRun.stale ? '#fcd34d' : '#c7d2fe',
          }}>
            <strong>{builderRun.stale ? 'Analysis stale' : builderRun.stale === false ? 'Analysis current' : 'Builder analysis'}</strong>
            {' · '}Portfolio Builder run {builderRun.runId.slice(0, 8)}: {builderRun.detail}. Pre-clear evaluates the live book either way.
            {builderRun.buyer && (
              <div style={{ marginTop: 4, color: '#cbd5e1' }}>
                Buyer of record: <strong>{builderRun.buyer}</strong>{builderRun.custody ? <> · settles to {builderRun.custody}</> : null}
              </div>
            )}
          </div>
        )}

        {workflowState.preClearBlock && (
          <OverrideBackdrop role="presentation">
            <OverrideDialog
              role="dialog"
              aria-modal="true"
              aria-labelledby="preclear-override-title"
              data-testid="preclear-override-dialog"
            >
              <OverrideDialogHeader>
                <OverrideIcon><ShieldCheck size={22} /></OverrideIcon>
                <div>
                  <div
                    id="preclear-override-title"
                    style={{ color: '#f8fafc', fontSize: 21, fontWeight: 750 }}
                  >
                    Pre-Clear Blocked — Override Required
                  </div>
                  <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 5, lineHeight: 1.45 }}>
                    This trade breaches governed compliance limits. You may edit the trade or
                    submit a documented request to the Chief Compliance Officer.
                  </div>
                </div>
              </OverrideDialogHeader>
              <OverrideBody>
                <OverrideMetrics>
                  <OverrideMetric>
                    <small>Commitment exposure</small>
                    <strong>${workflowState.preClearBlock.commitmentExposure.toLocaleString('en-US')}</strong>
                  </OverrideMetric>
                  <OverrideMetric>
                    <small>Funded par</small>
                    <strong>${workflowState.preClearBlock.fundedPar.toLocaleString('en-US')}</strong>
                  </OverrideMetric>
                  <OverrideMetric>
                    <small>Cash impact</small>
                    <strong>${workflowState.preClearBlock.cashImpact.toLocaleString('en-US')}</strong>
                  </OverrideMetric>
                </OverrideMetrics>

                <div style={{ color: '#cbd5e1', fontSize: 12, fontWeight: 750, textTransform: 'uppercase', letterSpacing: '.06em' }}>
                  Blocking rule checks
                </div>
                {workflowState.preClearBlock.breaches
                  .filter((breach, index, all) =>
                    all.findIndex(candidate => candidate.message === breach.message) === index
                  )
                  .map((breach, index) => (
                    <BreachCard key={`${breach.ruleId || breach.ruleKey || index}-${breach.message}`}>
                      <div style={{ color: '#fff1f2', fontWeight: 700 }}>
                        {breach.ruleName || breach.ruleKey || 'Compliance limit'}
                      </div>
                      <div style={{ marginTop: 3 }}>{breach.message}</div>
                      {breach.currentValue !== undefined && breach.limitValue !== undefined && breach.unit !== '' && (
                        <div style={{ marginTop: 7, color: '#fda4af', fontSize: 12 }}>
                          Proposed {formatBreachValue(breach.currentValue, breach.unit)} · Limit {formatBreachValue(breach.limitValue, breach.unit)}
                        </div>
                      )}
                    </BreachCard>
                  ))}
                {workflowState.preClearBlock.breaches.length >
                  new Set(workflowState.preClearBlock.breaches.map(breach => breach.message)).size && (
                  <div style={{ marginTop: 8, color: '#94a3b8', fontSize: 12 }}>
                    Duplicate economic messages are grouped here; every underlying governed rule
                    remains separate in the audit and CCO review queue.
                  </div>
                )}

                <div style={{ marginTop: 18 }}>
                  <Label htmlFor="overrideJustification">Business justification *</Label>
                  <TextArea
                    id="overrideJustification"
                    value={overrideJustification}
                    onChange={(event) => {
                      setOverrideJustification(event.target.value);
                      if (overrideJustificationError) setOverrideJustificationError('');
                    }}
                    placeholder="Explain why the investment remains appropriate despite the limit breach, including risk mitigants and expected remediation."
                    style={{ minHeight: 104, marginTop: 7 }}
                    autoFocus
                  />
                  {overrideJustificationError && (
                    <FieldError>{overrideJustificationError}</FieldError>
                  )}
                </div>

                <div style={{ marginTop: 13, color: '#94a3b8', fontSize: 12, lineHeight: 1.5 }}>
                  Submitting this request does not approve the trade. It moves the ticket to
                  Awaiting CCO Review. Compliance must approve every blocking rule, after which
                  the server reruns the complete pre-clear evaluation.
                </div>

                <OverrideActions>
                  <SecondaryButton
                    type="button"
                    onClick={() => {
                      workflowActions.clearPreClearBlock();
                      setOverrideJustification('');
                      setOverrideJustificationError('');
                    }}
                    disabled={workflowState.isLoading}
                  >
                    Continue Editing
                  </SecondaryButton>
                  <ActionButton
                    type="button"
                    variant="primary"
                    onClick={submitComplianceOverrideRequest}
                    disabled={workflowState.isLoading}
                    style={{ background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)' }}
                  >
                    {workflowState.isLoading && <LoadingSpinner />}
                    Submit Override Request
                  </ActionButton>
                </OverrideActions>
              </OverrideBody>
            </OverrideDialog>
          </OverrideBackdrop>
        )}

        {derivativeHeroConfig && (
          <DerivativeHeroPanel
            toneName={derivativeHeroConfig.toneName}
            icon={derivativeHeroConfig.icon}
            eyebrow={derivativeHeroConfig.eyebrow}
            title={derivativeHeroConfig.title}
            description={derivativeHeroConfig.description}
            badges={derivativeHeroConfig.badges}
            signals={derivativeHeroConfig.signals}
          />
        )}

        {Object.keys(workflowState.errors).filter(key => workflowState.errors[key]).length > 0 && (
          <ErrorSummary>
            <ErrorSummaryTitle>Unable to complete this action:</ErrorSummaryTitle>
            <ErrorList>
              {Object.entries(workflowState.errors)
                .filter(([, value]) => Boolean(value))
                .map(([key, value]) => (
                  <li key={key}>{value}</li>
                ))}
            </ErrorList>
          </ErrorSummary>
        )}

        {workflowState.showSuccess && (
          <SuccessMessage>
            {workflowState.successMessage}
          </SuccessMessage>
        )}

        {shortcutFeedback && (
          <ShortcutFeedback $tone={shortcutFeedback.tone}>
            {shortcutFeedback.message}
          </ShortcutFeedback>
        )}

        {/* Pre-Clear Status Messages */}
        {workflowState.transactionStatus === 'PRECLEAR_PENDING'
          && workflowState.overrideRequestPending && (
          <div style={{
            background: 'rgba(245, 158, 11, 0.1)',
            border: '1px solid rgba(245, 158, 11, 0.35)',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1.5rem',
            color: '#fbbf24'
          }}>
            🛡️ <strong>Override Requested — Awaiting CCO:</strong> This trade remains blocked.
            Compliance must review every breached rule before pre-clear can be approved.
          </div>
        )}
        {workflowState.transactionStatus === 'PRECLEAR_PENDING'
          && !workflowState.overrideRequestPending && (
          <div style={{
            background: 'rgba(139, 92, 246, 0.1)',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1.5rem',
            color: '#a78bfa'
          }}>
            ⏳ <strong>Pre-Clear Pending:</strong> This trade is awaiting compliance review. A Compliance Officer must approve or reject it before it can be released.
          </div>
        )}
        {workflowState.transactionStatus === 'PRECLEAR_APPROVED' && (
          <div style={{
            background: 'rgba(16, 185, 129, 0.1)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1.5rem',
            color: '#10b981'
          }}>
            ✅ <strong>Pre-Clear Approved:</strong> This trade has been approved by compliance and is ready to be released to the broker.
          </div>
        )}
        {preClearRules && preClearRules.length > 0 && (() => {
          const shown = preClearRulesOpen ? preClearRules : preClearRules.filter((r: any) => String(r.ruleKey || '').startsWith('clo_') || r.passed === false);
          const hiddenPassing = preClearRules.length - shown.length;
          const show = (v: any, unit?: string) => {
            if (v === null || v === undefined || v === '') return '—';
            const n = Number(v); if (!Number.isFinite(n)) return String(v);
            switch (unit) {
              case '%': return `${n.toFixed(2)}%`; case 'bps': return `${n.toFixed(1)} bps`; case 'years': return `${n.toFixed(2)} yrs`;
              case 'factor': return n.toFixed(0); case 'USD': return `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
              case 'x': return `${n.toFixed(3)}x`; case 'score': return n.toFixed(2); default: return `${n}`;
            }
          };
          return (
            <div style={{ border: '1px solid rgba(148,163,184,0.18)', borderRadius: 10, padding: '12px 14px', marginBottom: '1.5rem', background: 'rgba(15,23,42,0.5)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: '#e2e8f0', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Pre-clear result · {preClearRules.filter((r: any) => r.passed).length} of {preClearRules.length} rules pass</div>
                <div style={{ fontSize: 11, color: '#94a3b8' }}>The numbers compliance approved against, on the live book at submission.</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 0.8fr 0.8fr 0.8fr 0.6fr 2fr', gap: '0 12px', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', padding: '8px 0 6px', borderBottom: '1px solid rgba(148,163,184,0.12)' }}>
                <div>Rule</div><div style={{ textAlign: 'right' }}>Before</div><div style={{ textAlign: 'right' }}>After</div><div style={{ textAlign: 'right' }}>Limit</div><div style={{ textAlign: 'right' }}>Result</div><div>Note</div>
              </div>
              {shown.map((r: any, i: number) => (
                <div key={`${r.ruleId}-${i}`} style={{ display: 'grid', gridTemplateColumns: '1.6fr 0.8fr 0.8fr 0.8fr 0.6fr 2fr', gap: '0 12px', fontSize: 12, padding: '7px 0', borderBottom: '1px solid rgba(148,163,184,0.08)', alignItems: 'start' }}>
                  <div style={{ color: '#e2e8f0', fontWeight: 600 }}>{r.ruleName}{r.className ? ` · ${r.className}` : ''}</div>
                  <div style={{ textAlign: 'right', color: '#94a3b8', fontFamily: 'monospace' }}>{show(r.beforeValue, r.unit)}</div>
                  <div style={{ textAlign: 'right', color: '#f8fafc', fontFamily: 'monospace' }}>{show(r.currentValue, r.unit)}</div>
                  <div style={{ textAlign: 'right', color: '#94a3b8', fontFamily: 'monospace' }}>{show(r.limitValue, r.unit)}</div>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.06em', padding: '2px 7px', borderRadius: 6,
                      background: r.passed ? 'rgba(16,185,129,0.12)' : r.evaluationStatus === 'UNKNOWN' ? 'rgba(245,158,11,0.12)' : 'rgba(239,68,68,0.12)',
                      color: r.passed ? '#34d399' : r.evaluationStatus === 'UNKNOWN' ? '#fcd34d' : '#f87171' }}>
                      {r.passed ? 'PASS' : r.evaluationStatus === 'UNKNOWN' ? 'UNKNOWN' : r.evaluationStatus === 'NOT_APPLICABLE' ? 'N/A' : (r.breachAction === 'BLOCK' ? 'FAIL' : r.breachAction || 'FAIL')}
                    </span>
                  </div>
                  <div style={{ color: '#94a3b8', lineHeight: 1.35 }}>{Array.isArray(r.missing) && r.missing.length > 0 ? <span style={{ color: '#fcd34d' }}>Needs {r.missing.join(', ')}. </span> : null}{r.message}</div>
                </div>
              ))}
              {(hiddenPassing > 0 || preClearRulesOpen) && (
                <button type="button" onClick={() => setPreClearRulesOpen(o => !o)} style={{ background: 'none', border: 'none', color: '#a5b4fc', fontSize: 12, padding: '8px 0 0', cursor: 'pointer' }}>
                  {preClearRulesOpen ? 'Hide the other passing rules' : `Show ${hiddenPassing} other passing rules`}
                </button>
              )}
            </div>
          );
        })()}
        {workflowState.transactionStatus === 'PRECLEAR_REJECTED' && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1.5rem',
            color: '#f87171'
          }}>
            ❌ <strong>Pre-Clear Rejected:</strong> This trade was rejected during compliance review. Please review the rejection reason and make necessary changes.
          </div>
        )}

        {/* Prospective Portfolio Impact Panel */}
        {(prospectiveImpact || prospectiveLoading) && (
          <ProspectiveImpactPanel>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <BarChart3 style={{ width: '16px', height: '16px', color: '#f59e0b' }} />
              <span style={{ fontSize: '14px', fontWeight: 700, color: '#f9fafb' }}>
                Prospective Portfolio Impact
              </span>
              {prospectiveLoading && (
                <span style={{ fontSize: '11px', color: '#6b7280', marginLeft: '8px' }}>Calculating...</span>
              )}
            </div>
            {prospectiveImpact && !prospectiveLoading && (
              <div>
                {/* Issuer Concentration */}
                {prospectiveImpact.tradeIssuerDelta && (
                  <ImpactRow>
                    <span style={{ color: '#9ca3af' }}>
                      Issuer: <strong style={{ color: '#e5e7eb' }}>{prospectiveImpact.tradeIssuerDelta.key}</strong>
                    </span>
                    <span>
                      <span style={{ color: '#9ca3af' }}>{prospectiveImpact.tradeIssuerDelta.beforePct?.toFixed(2)}%</span>
                      <span style={{ color: '#6b7280', margin: '0 6px' }}>&rarr;</span>
                      <span style={{ color: '#f9fafb', fontWeight: 600 }}>{prospectiveImpact.tradeIssuerDelta.afterPct?.toFixed(2)}%</span>
                      <span style={{
                        marginLeft: '8px',
                        fontSize: '12px',
                        fontWeight: 700,
                        color: (prospectiveImpact.tradeIssuerDelta.deltaPct || 0) > 0 ? '#f59e0b' : '#10b981'
                      }}>
                        {(prospectiveImpact.tradeIssuerDelta.deltaPct || 0) > 0 ? '+' : ''}
                        {prospectiveImpact.tradeIssuerDelta.deltaPct?.toFixed(2)}pp
                      </span>
                    </span>
                  </ImpactRow>
                )}

                {/* Sector Concentration */}
                {prospectiveImpact.tradeSectorDelta && (
                  <ImpactRow>
                    <span style={{ color: '#9ca3af' }}>
                      Sector: <strong style={{ color: '#e5e7eb' }}>{prospectiveImpact.tradeSectorDelta.key}</strong>
                    </span>
                    <span>
                      <span style={{ color: '#9ca3af' }}>{prospectiveImpact.tradeSectorDelta.beforePct?.toFixed(2)}%</span>
                      <span style={{ color: '#6b7280', margin: '0 6px' }}>&rarr;</span>
                      <span style={{ color: '#f9fafb', fontWeight: 600 }}>{prospectiveImpact.tradeSectorDelta.afterPct?.toFixed(2)}%</span>
                      <span style={{
                        marginLeft: '8px',
                        fontSize: '12px',
                        fontWeight: 700,
                        color: (prospectiveImpact.tradeSectorDelta.deltaPct || 0) > 0 ? '#f59e0b' : '#10b981'
                      }}>
                        {(prospectiveImpact.tradeSectorDelta.deltaPct || 0) > 0 ? '+' : ''}
                        {prospectiveImpact.tradeSectorDelta.deltaPct?.toFixed(2)}pp
                      </span>
                    </span>
                  </ImpactRow>
                )}

                {/* Duration & DV01 */}
                {(prospectiveImpact.durationBefore != null || prospectiveImpact.dv01Before != null) && (
                  <>
                    <ImpactRow>
                      <span style={{ color: '#9ca3af' }}>Duration</span>
                      <span>
                        <span style={{ color: '#9ca3af' }}>{prospectiveImpact.durationBefore?.toFixed(3)}</span>
                        <span style={{ color: '#6b7280', margin: '0 6px' }}>&rarr;</span>
                        <span style={{ color: '#f9fafb', fontWeight: 600 }}>{prospectiveImpact.durationAfter?.toFixed(3)}</span>
                        <span style={{
                          marginLeft: '8px',
                          fontSize: '12px',
                          fontWeight: 700,
                          color: (prospectiveImpact.durationAfter - prospectiveImpact.durationBefore) > 0 ? '#f59e0b' : '#10b981'
                        }}>
                          {(prospectiveImpact.durationAfter - prospectiveImpact.durationBefore) > 0 ? '+' : ''}
                          {(prospectiveImpact.durationAfter - prospectiveImpact.durationBefore).toFixed(3)}
                        </span>
                      </span>
                    </ImpactRow>
                    <ImpactRow>
                      <span style={{ color: '#9ca3af' }}>DV01</span>
                      <span>
                        <span style={{ color: '#9ca3af' }}>${prospectiveImpact.dv01Before?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                        <span style={{ color: '#6b7280', margin: '0 6px' }}>&rarr;</span>
                        <span style={{ color: '#f9fafb', fontWeight: 600 }}>${prospectiveImpact.dv01After?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                      </span>
                    </ImpactRow>
                  </>
                )}

                {/* Rating Distribution */}
                {prospectiveImpact.ratingDistribution && (
                  <ImpactRow>
                    <span style={{ color: '#9ca3af' }}>Rating Shift</span>
                    <span style={{ fontSize: '12px' }}>
                      <span style={{ color: '#10b981' }}>IG {prospectiveImpact.ratingDistribution.igPctBefore?.toFixed(1)}&rarr;{prospectiveImpact.ratingDistribution.igPctAfter?.toFixed(1)}%</span>
                      <span style={{ color: '#6b7280', margin: '0 8px' }}>|</span>
                      <span style={{ color: '#f59e0b' }}>HY {prospectiveImpact.ratingDistribution.hyPctBefore?.toFixed(1)}&rarr;{prospectiveImpact.ratingDistribution.hyPctAfter?.toFixed(1)}%</span>
                      <span style={{ color: '#6b7280', margin: '0 8px' }}>|</span>
                      <span style={{ color: '#9ca3af' }}>NR {prospectiveImpact.ratingDistribution.nrPctBefore?.toFixed(1)}&rarr;{prospectiveImpact.ratingDistribution.nrPctAfter?.toFixed(1)}%</span>
                    </span>
                  </ImpactRow>
                )}

                {/* Cash Impact */}
                {prospectiveImpact.cashImpact != null && (
                  <ImpactRow>
                    <span style={{ color: '#9ca3af' }}>Cash Impact</span>
                    <span style={{
                      fontWeight: 700,
                      color: prospectiveImpact.cashImpact < 0 ? '#ef4444' : '#10b981'
                    }}>
                      {prospectiveImpact.cashImpact < 0 ? '-' : '+'}${Math.abs(prospectiveImpact.cashImpact).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </span>
                  </ImpactRow>
                )}
              </div>
            )}
          </ProspectiveImpactPanel>
        )}

        {/* AI Trade Suggestions Panel */}
        {showSuggestions && (
          <FullWidthSection style={{ marginBottom: '24px', background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', border: '2px solid #0ea5e9', padding: '20px', borderRadius: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, color: '#0c4a6e', fontSize: '20px', fontWeight: '600' }}>🤖 AI Trade Suggestions</h3>
              <button 
                onClick={() => setShowSuggestions(false)} 
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  cursor: 'pointer', 
                  fontSize: '24px',
                  color: '#0c4a6e',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f0f9ff'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
              >
                ×
              </button>
            </div>
            
            {loadingSuggestions ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                <div style={{ fontSize: '18px', marginBottom: '12px' }}>🔄</div>
                <div>Generating AI suggestions...</div>
                <div style={{ fontSize: '12px', marginTop: '8px', color: '#94a3b8' }}>This may take 10-30 seconds</div>
              </div>
            ) : tradeSuggestions.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {tradeSuggestions.map((suggestion: any, idx: number) => (
                  <div 
                    key={idx} 
                    style={{ 
                      padding: '16px', 
                      background: 'white', 
                      borderRadius: '8px', 
                      border: '1px solid #bae6fd',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(14, 165, 233, 0.2)';
                      e.currentTarget.style.borderColor = '#0ea5e9';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.borderColor = '#bae6fd';
                    }}
                    onClick={() => {
                      // Populate form with suggestion
                      if (suggestion.securityCusip) handleInputChange('cusip', suggestion.securityCusip);
                      if (suggestion.transactionType) handleInputChange('transactionType', suggestion.transactionType);
                      if (suggestion.amount) handleInputChange('amount', suggestion.amount.toString());
                      if (suggestion.rationale) handleInputChange('rationale', suggestion.rationale);
                      setShowSuggestions(false);
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <strong style={{ color: '#0c4a6e', fontSize: '16px' }}>{suggestion.securityName || suggestion.securityCusip}</strong>
                      <span style={{ 
                        padding: '4px 12px', 
                        borderRadius: '12px', 
                        background: suggestion.transactionType === 'BUY' ? '#dcfce7' : '#fee2e2',
                        color: suggestion.transactionType === 'BUY' ? '#166534' : '#991b1b',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {suggestion.transactionType}
                      </span>
                    </div>
                    <div style={{ color: '#475569', fontSize: '14px', marginBottom: '8px' }}>
                      <strong>Amount:</strong> ${suggestion.amount?.toLocaleString()}
                      {suggestion.securityCusip && (
                        <span style={{ marginLeft: '12px', color: '#64748b' }}>
                          <strong>CUSIP:</strong> {suggestion.securityCusip}
                        </span>
                      )}
                    </div>
                    {suggestion.rationale && (
                      <div style={{ color: '#64748b', fontSize: '13px', fontStyle: 'italic', marginTop: '8px', padding: '8px', background: '#f8fafc', borderRadius: '4px' }}>
                        <strong>Rationale:</strong> {suggestion.rationale}
                      </div>
                    )}
                    {suggestion.expectedImpact && (
                      <div style={{ color: '#0ea5e9', fontSize: '12px', marginTop: '8px', fontWeight: '500' }}>
                        <strong>Expected Impact:</strong> {suggestion.expectedImpact}
                      </div>
                    )}
                    <div style={{ marginTop: '12px', fontSize: '12px', color: '#0ea5e9', fontWeight: '600' }}>
                      👆 Click to use this suggestion
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                <div style={{ fontSize: '18px', marginBottom: '12px' }}>💡</div>
                <div>No suggestions available.</div>
                <div style={{ fontSize: '12px', marginTop: '8px', color: '#94a3b8' }}>Try selecting a portfolio and clicking "Get AI Trade Suggestions" again.</div>
              </div>
            )}
          </FullWidthSection>
        )}

        <FormContainer>
          {children({
            formData,
            setFormData,
            errors: workflowState.errors,
            setErrors: workflowActions.setErrors,
            dimensionData,
            transactionStatus: workflowState.transactionStatus,
            editingTransactionId: workflowState.editingTransactionId,
            isLoading: workflowState.isLoading,
            isFieldDisabled,
            handleInputChange,
            handleInputBlur,
            validateForm,
            getButtonStates,
            useAllocations,
            allocations,
            setAllocations,
            setUseAllocations,
            // Household data for retail
            households,
            selectedHousehold,
            setSelectedHousehold,
            filteredPortfolios
          })}

          {!suppressExecutionClearingSection && (
            <ExecutionClearingSection
              formData={formData}
              errors={workflowState.errors}
              isFieldDisabled={isFieldDisabled}
              handleInputChange={handleInputChange}
              config={executionClearingConfig}
            />
          )}
          
          {/* Portfolio/Account Selection (if not using allocations and not hidden) */}
          {!useAllocations && !hidePortfolioField && (
            <>
              {isRetail ? (
                /* Retail: Household + Client Account in a row */
                <FormRow>
                  <FormGroup>
                    <Label htmlFor="household">Household</Label>
                    <Select
                      id="household"
                      value={selectedHousehold}
                      onChange={(e) => setSelectedHousehold(e.target.value)}
                      disabled={isFieldDisabled('portfolio')}
                    >
                      <option value="">All Households</option>
                      {households.map((household: any) => (
                        <option key={household.id} value={household.id.toString()}>
                          {household.householdName || household.name}
                        </option>
                      ))}
                    </Select>
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="portfolio">Client Account*</Label>
                    <Select
                      id="portfolio"
                      value={formData.portfolio}
                      onChange={(e) => handleInputChange('portfolio', e.target.value)}
                      className={workflowState.errors.portfolio ? 'error' : ''}
                      disabled={isFieldDisabled('portfolio')}
                    >
                      <option value="">Select Account</option>
                      {(selectedHousehold ? filteredPortfolios : dimensionData.portfolios).map((p: any) => (
                        <option key={p.id} value={p.portfolioName}>
                          {p.portfolioName}
                        </option>
                      ))}
                    </Select>
                    {workflowState.errors.portfolio && <FieldError>{workflowState.errors.portfolio}</FieldError>}
                  </FormGroup>
                </FormRow>
              ) : (
                /* Institutional: Portfolio dropdown */
                <FullWidthSection>
                  <FormGroup>
                    <Label htmlFor="portfolio">Portfolio*</Label>
                    <Select
                      id="portfolio"
                      value={formData.portfolio}
                      onChange={(e) => handleInputChange('portfolio', e.target.value)}
                      className={workflowState.errors.portfolio ? 'error' : ''}
                      disabled={isFieldDisabled('portfolio')}
                    >
                      <option value="">Select Portfolio</option>
                      {dimensionData.portfolios.map((p: any) => (
                        <option key={p.id} value={p.portfolioName}>
                          {p.portfolioName}
                        </option>
                      ))}
                    </Select>
                    {workflowState.errors.portfolio && <FieldError>{workflowState.errors.portfolio}</FieldError>}
                  </FormGroup>
                </FullWidthSection>
              )}
            </>
          )}
          
          {/* Allocation Grid */}
          <FullWidthSection>
            <ExpanderHeader onClick={() => toggleSection('allocations')}>
              {expandedSections.allocations ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              <span>{isRetail ? 'Model Portfolio / Multi-Account Trade' : 'Allocation Grid'}</span>
            </ExpanderHeader>
            {expandedSections.allocations && (
              <ExpanderContent>
                <AllocationPanel style={{ marginTop: 0 }}>
                  <AllocationHeader>
                    <AllocationTitle>{isRetail ? 'Model Portfolio / Multi-Account Trade' : 'Allocation Grid'}</AllocationTitle>
                    <ToggleButton
                      active={useAllocations}
                      onClick={() => {
                        setUseAllocations(!useAllocations);
                        if (!useAllocations && allocations.length === 0) {
                          setAllocations([{ portfolioId: '', portfolioName: '', allocatedAmount: '', notes: '' }]);
                        }
                      }}
                    >
                      {useAllocations ? '✓ Using Allocations' : 'Use Allocations'}
                    </ToggleButton>
                  </AllocationHeader>

                  {useAllocations && (
                    <>
                      <AllocationGrid>
                        <AllocationGridHeader>{isRetail ? 'Client Account' : 'Portfolio'}</AllocationGridHeader>
                        <AllocationGridHeader>Allocated Amount</AllocationGridHeader>
                        <AllocationGridHeader>Percentage</AllocationGridHeader>
                        <AllocationGridHeader></AllocationGridHeader>
                        
                        {allocations.map((allocation, index) => {
                          const blockAmount = parseAmountShorthand(formData.amount) || 0;
                          const allocatedAmount = parseAmountShorthand(allocation.allocatedAmount) || 0;
                          const percentage = blockAmount > 0 ? ((allocatedAmount / blockAmount) * 100).toFixed(2) : '0.00';
                          
                          return (
                            <AllocationRow key={index}>
                              <AllocationSelect
                                value={allocation.portfolioId}
                                onChange={(e) => {
                                  const portfolio = dimensionData.portfolios
                                    .find((p: any) => p.id.toString() === e.target.value);
                                  const newAllocations = [...allocations];
                                  newAllocations[index] = {
                                    ...newAllocations[index],
                                    portfolioId: e.target.value,
                                    portfolioName: portfolio ? portfolio.portfolioName : ''
                                  };
                                  setAllocations(newAllocations);
                                }}
                                disabled={isFieldDisabled('portfolio')}
                              >
                                <option value="">{isRetail ? 'Select Account' : 'Select Portfolio'}</option>
                                {(() => {
                                  // Get base portfolio list (filtered by household or all)
                                  const basePortfolios = isRetail && selectedHousehold ? filteredPortfolios : dimensionData.portfolios;
                                  // Get IDs of portfolios already in allocations (to ensure they're always shown)
                                  const allocatedPortfolioIds = allocations.map(a => a.portfolioId);
                                  // Include both filtered portfolios AND any portfolios already in allocations
                                  const availablePortfolios = dimensionData.portfolios.filter((p: any) => {
                                    const isInBase = basePortfolios.some((bp: any) => bp.id === p.id);
                                    const isAllocated = allocatedPortfolioIds.includes(p.id.toString());
                                    return isInBase || isAllocated;
                                  });
                                  return availablePortfolios
                                    .filter((p: any) => !allocations.some((a, i) => i !== index && a.portfolioId === p.id.toString()))
                                    .map((p: any) => (
                                      <option key={p.id} value={p.id}>
                                        {p.portfolioName}
                                      </option>
                                    ));
                                })()}
                              </AllocationSelect>
                              <AllocationInput
                                type="text"
                                value={allocation.allocatedAmount}
                                onChange={(e) => {
                                  const newAllocations = [...allocations];
                                  newAllocations[index] = { ...newAllocations[index], allocatedAmount: e.target.value };
                                  setAllocations(newAllocations);
                                }}
                                onBlur={(e) => {
                                  const parsed = parseAmountShorthand(e.target.value);
                                  if (parsed > 0) {
                                    const newAllocations = [...allocations];
                                    newAllocations[index] = { ...newAllocations[index], allocatedAmount: formatAmountWithCommas(parsed) };
                                    setAllocations(newAllocations);
                                  }
                                }}
                                placeholder="Amount (e.g., 1M, 1MM)"
                                disabled={isFieldDisabled('amount')}
                              />
                              <div style={{ padding: '12px 16px', fontSize: '14px', fontWeight: 600, color: '#64748b' }}>
                                {percentage}%
                              </div>
                              <AllocationRemoveButton
                                type="button"
                                onClick={() => {
                                  const newAllocations = allocations.filter((_, i) => i !== index);
                                  setAllocations(newAllocations);
                                }}
                                disabled={allocations.length === 1}
                              >
                                Remove
                              </AllocationRemoveButton>
                            </AllocationRow>
                          );
                        })}
                      </AllocationGrid>
                      
                      <AddAllocationButton
                        type="button"
                        onClick={() => {
                          setAllocations([...allocations, { portfolioId: '', portfolioName: '', allocatedAmount: '', notes: '' }]);
                        }}
                        disabled={isFieldDisabled('portfolio')}
                      >
                        + Add Allocation
                      </AddAllocationButton>
                      
                      <AllocationSummary>
                        <AllocationSummaryLabel>Total Allocated:</AllocationSummaryLabel>
                        <AllocationSummaryValue valid={Math.abs(allocations.reduce((sum, a) => sum + (parseAmountShorthand(a.allocatedAmount) || 0), 0) - (parseAmountShorthand(formData.amount) || 0)) < 0.01}>
                          ${allocations.reduce((sum, a) => sum + (parseAmountShorthand(a.allocatedAmount) || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / {(parseAmountShorthand(formData.amount) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </AllocationSummaryValue>
                      </AllocationSummary>
                    </>
                  )}
                </AllocationPanel>
              </ExpanderContent>
            )}
          </FullWidthSection>
          
          {/* Notes Section */}
          <FullWidthSection>
            <ExpanderHeader onClick={() => toggleSection('notes')}>
              {expandedSections.notes ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              <span>Notes</span>
            </ExpanderHeader>
            {expandedSections.notes && (
              <ExpanderContent>
                <FormGroup>
                  <TextArea
                    id="notes"
                    value={formData.notes || ''}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    placeholder="Enter any additional information"
                    disabled={isFieldDisabled('notes')}
                  />
                </FormGroup>
              </ExpanderContent>
            )}
          </FullWidthSection>
          
          {/* AI Trade Rationale Section */}
          <FullWidthSection>
            <ExpanderHeader onClick={() => toggleSection('rationale')}>
              {expandedSections.rationale ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              <span>Trade Rationale</span>
            </ExpanderHeader>
            {expandedSections.rationale && (
              <ExpanderContent>
                <FormGroup>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <SecondaryButton
                        type="button"
                        onClick={async () => {
                          setGeneratingRationale(true);
                          setBlockingOperation({
                            isActive: true,
                            message: 'Generating AI Rationale...',
                            subMessage: 'Analyzing trade context'
                          });
                          try {
                            // Build transaction data for the API call
                            const transactionData = await buildTransactionData(formData, dimensionData, useAllocations);
                            const response = await axios.post('/api/ai/generate-rationale', transactionData);
                            if (response.data.rationale) {
                              handleInputChange('rationale', response.data.rationale);
                            } else {
                              alert('Failed to generate rationale: ' + (response.data.error || 'Unknown error'));
                            }
                          } catch (error: any) {
                            console.error('Error generating rationale:', error);
                            alert('Error generating rationale: ' + (error.response?.data?.error || error.message || 'Unknown error'));
                          } finally {
                            setGeneratingRationale(false);
                            setBlockingOperation({ isActive: false, message: '' });
                          }
                        }}
                        disabled={generatingRationale || isFieldDisabled('rationale')}
                        style={{ padding: '6px 12px', fontSize: '13px' }}
                      >
                        {generatingRationale ? '🔄 Generating...' : 'Auto-Generate'}
                      </SecondaryButton>
                      <SecondaryButton
                        type="button"
                        onClick={async () => {
                          setGeneratingRationale(true);
                          setBlockingOperation({
                            isActive: true,
                            message: 'Generating Enhanced Rationale...',
                            subMessage: 'Performing multi-step AI research analysis'
                          });
                          try {
                            // Build transaction data for the API call
                            const transactionData = await buildTransactionData(formData, dimensionData, useAllocations);
                            const response = await axios.post('/api/ai/enhanced-rationale', transactionData);
                            if (response.data.success && response.data.rationale) {
                              handleInputChange('rationale', response.data.rationale);
                            } else {
                              alert('Failed to generate enhanced rationale: ' + (response.data.error || 'Unknown error'));
                            }
                          } catch (error: any) {
                            console.error('Error generating enhanced rationale:', error);
                            alert('Error generating enhanced rationale: ' + (error.response?.data?.error || error.message || 'Unknown error'));
                          } finally {
                            setGeneratingRationale(false);
                            setBlockingOperation({ isActive: false, message: '' });
                          }
                        }}
                        disabled={generatingRationale || isFieldDisabled('rationale')}
                        style={{ padding: '6px 12px', fontSize: '13px' }}
                      >
                        {generatingRationale ? '🔄 Generating...' : 'Deep Research'}
                      </SecondaryButton>
                    </div>
                  </div>
                  <TextArea
                    id="rationale"
                    value={formData.rationale || ''}
                    onChange={(e) => handleInputChange('rationale', e.target.value)}
                    placeholder="Enter trade rationale manually or auto-generate"
                    disabled={isFieldDisabled('rationale')}
                    style={{ minHeight: '80px' }}
                  />
                </FormGroup>
              </ExpanderContent>
            )}
          </FullWidthSection>
          
          {/* Attachments Section */}
          <FullWidthSection>
            <ExpanderHeader onClick={() => toggleSection('attachments')}>
              {expandedSections.attachments ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              <span>Attachments</span>
            </ExpanderHeader>
            {expandedSections.attachments && (
              <ExpanderContent>
                <FormGroup>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <FileUploadArea>
                      <input
                        type="file"
                        onChange={handleFileSelect}
                        disabled={!workflowState.editingTransactionId || uploadingFile}
                      />
                      <FileUploadText>
                        <span>📎</span>
                        {uploadingFile ? 'Uploading...' : workflowState.editingTransactionId ? 'Click to select file' : 'Draft transaction first to upload files'}
                      </FileUploadText>
                    </FileUploadArea>
                    {attachments.length > 0 && (
                      <FileList>
                        {attachments.map((attachment) => (
                          <FileItem key={attachment.id}>
                            <FileInfo>
                              <FileIcon>{getFileIcon(attachment.fileName)}</FileIcon>
                              <FileDetails>
                                <FileName>{attachment.fileName}</FileName>
                                <FileSize>{formatFileSize(attachment.fileSize || 0)}</FileSize>
                              </FileDetails>
                            </FileInfo>
                            <FileRemoveButton onClick={() => handleRemoveAttachment(attachment.id)}>
                              Remove
                            </FileRemoveButton>
                          </FileItem>
                        ))}
                      </FileList>
                    )}
                  </div>
                </FormGroup>
              </ExpanderContent>
            )}
          </FullWidthSection>
        </FormContainer>

        {/* F-REPO-019: errors.submit was set in six places but rendered
            NOWHERE — failed draft saves, uploads and allocation errors were
            all silent. One banner above the action bar, cleared per attempt. */}
        {workflowState.errors?.submit && (
          <div
            data-testid="ticket-submit-error"
            style={{
              margin: '10px 0',
              padding: '10px 14px',
              borderRadius: 10,
              border: '1px solid rgba(239,68,68,0.45)',
              background: 'rgba(239,68,68,0.12)',
              color: '#fca5a5',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            ❌ {workflowState.errors.submit}
          </div>
        )}
        <ActionButtonsWrapper>
          <ActionButtons>
            <div style={{ display: 'flex', gap: '12px' }}>
              {canDraft && (
                <ActionButton
                  variant="primary"
                  data-testid="ticket-action-draft"
                  onClick={() => handleSubmit('draft')}
                  disabled={!buttonStates.draft || workflowState.isLoading}
                >
                  {workflowState.isLoading && <LoadingSpinner />}
                  {workflowState.editingTransactionId ? 'Update Draft' : 'Draft'}
                </ActionButton>
              )}
              {canSubmitPreClear && !lateBookingControl?.active && (
                <ActionButton
                  variant="primary"
                  data-testid="ticket-action-submitForPreClear"
                  onClick={() => handleSubmit('submitForPreClear')}
                  disabled={!buttonStates.submitForPreClear || workflowState.isLoading}
                  style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)' }}
                >
                  {workflowState.isLoading && <LoadingSpinner />}
                  Submit for Pre-Clear
                </ActionButton>
              )}
              {canApproveReject && (
                <>
                  <ActionButton
                    variant="primary"
                    data-testid="ticket-action-approvePreClear"
                    onClick={() => handleSubmit('approvePreClear')}
                    disabled={!buttonStates.approvePreClear || workflowState.isLoading}
                    style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
                  >
                    {workflowState.isLoading && <LoadingSpinner />}
                    ✓ Approve Pre-Clear
                  </ActionButton>
                  <ActionButton
                    variant="danger"
                    onClick={() => handleSubmit('rejectPreClear')}
                    disabled={!buttonStates.rejectPreClear || workflowState.isLoading}
                  >
                    {workflowState.isLoading && <LoadingSpinner />}
                    ✗ Reject Pre-Clear
                  </ActionButton>
                </>
              )}
              {canSend && (
                <ActionButton
                  variant="primary"
                  data-testid="ticket-action-send"
                  onClick={() => handleSubmit('send')}
                  disabled={!buttonStates.send || workflowState.isLoading}
                >
                  Release
                </ActionButton>
              )}
              {canExecute && (
                <ActionButton
                  variant="primary"
                  data-testid="ticket-action-execute"
                  onClick={() => handleSubmit('execute')}
                  disabled={!buttonStates.execute || workflowState.isLoading}
                >
                  Execute
                </ActionButton>
              )}
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <ActionButton
                variant="danger"
                onClick={() => handleSubmit('void')}
                disabled={!buttonStates.void || workflowState.isLoading}
              >
                Void
              </ActionButton>
              <ActionButton
                variant="danger"
                onClick={() => handleSubmit('reject')}
                disabled={!buttonStates.reject || workflowState.isLoading}
              >
                Reject
              </ActionButton>
              <ActionButton
                variant="danger"
                data-testid="ticket-action-reverse"
                onClick={handleReverse}
                disabled={!buttonStates.reverse || workflowState.isLoading}
              >
                Reverse
              </ActionButton>
              <ActionButton
                onClick={handleDuplicateTicket}
                disabled={!buttonStates.duplicate || workflowState.isLoading}
              >
                Duplicate
              </ActionButton>
              <ActionButton onClick={() => navigate('/bond-transactions')}>
                Cancel
              </ActionButton>
            </div>
          </ActionButtons>
        </ActionButtonsWrapper>
        </MainContent>
      </ContentWrapper>
      
      {/* Audit History Drawer */}
      <AuditHistoryDrawer
        isOpen={isAuditDrawerOpen}
        onClose={() => setIsAuditDrawerOpen(false)}
        tradeId={workflowState.editingTransactionId}
        ticketNumber={workflowState.editingTransactionId?.toString()}
      />
    </Container>
  );
};

export default BaseTicketScreen;
